import {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Inbox,
  AlertCircle,
  Loader2,
  X,
  Building2,
  CalendarRange,
  Activity,
  TrendingDown,
  AlertTriangle,
  ShieldCheck,
  Flame,
  FileWarning,
  ArrowUpRight,
  Minus,
  Plus,
  RotateCcw,
  Server,
  AppWindow,
  Shield,
  ShieldAlert,
  ExternalLink,
  GitCompare,
  Layers,
} from "lucide-react";
import { useNavigate } from "react-router";
import { useReducedMotion } from "motion/react";
import { DonutPieChart } from "../components/ChartsComponent";
import { API_URL } from "../../constants";
import { getAPI } from "../../api/frameworksApi";
import { labelToDomId } from "../../utils/formatters";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { Dropdown } from "../components/Dropdown";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { OrgDetailsDrawer } from "./quadrant/OrgDetailsDrawer";
import { CatalogCompareView } from "./quadrant/components/CatalogCompareView";
import {
  EMPTY_SCOPE,
  ORG_AXIS_METRICS,
  normalizeOrgRow,
  orgCoverageMeta,
  scopeQuery,
  scopeQueryFirst,
  type FilterScopeData,
  type OrgCoverageStatus,
  type OrgDrillDownData,
  type OrgQuadrantData,
  type OrgQuadrantSummaryData,
  type QuadrantScopeOptionsData,
  type QuadrantScopeSelection,
} from "./quadrant/orgScope";

/** Bubbles on the plot. Beyond this they cluster into unreadable overlap; the
 *  rest are reachable from the Top Risk panel's View More drawer. */
const QUADRANT_PLOT_TOP_N = 10;

const DEFAULT_THRESHOLD = 15;
const THRESHOLD_MIN = 1;
const THRESHOLD_MAX = 100;

/** Shows a tooltip with the full name only when the truncated span is
 *  actually clipping text — an untruncated name gets no tooltip at all. */
function TruncatedName({ name, className }: { name: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  const isOverflowing = () => {
    const el = ref.current;
    return !!el && el.scrollWidth > el.clientWidth + 1;
  };

  return (
    <Tooltip
      open={open}
      onOpenChange={(next) => setOpen(next && isOverflowing())}
    >
      <TooltipTrigger asChild>
        <span ref={ref} className={className}>
          {name}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={6}
        className="max-w-md text-left whitespace-pre-wrap break-words"
      >
        {name}
      </TooltipContent>
    </Tooltip>
  );
}

type RangeFilter = "DAY" | "WEEK" | "MONTH" | "YEAR";
/**
 * Which entity the chart plots. `APPLICATION` is the original view.
 * `ORGANIZATION` is the same chart one level up — bubbles are organizations,
 * optionally narrowed to one GROUP via the group switcher.
 */
type SourceType = "APPLICATION" | "ORGANIZATION";
type ZoneKey = "CRITICAL" | "EMERGING RISK" | "IMPROVING" | "HEALTHY";

/* Labels are one rung above the wire value on purpose. `source_type` names the
   entity each bubble IS (an application, an organization), while the picker
   names the scope the reader is looking ACROSS — the organizations inside a
   group, or the group as a whole. Only the labels shift; the ids still go over
   the wire unchanged. */
const SOURCE_OPTIONS: { id: SourceType; label: string }[] = [
  { id: "APPLICATION", label: "Organizations" },
  { id: "ORGANIZATION", label: "Group" },
];

/** Every org-scope request sends this as `source_type` on the app endpoints. */
const APPLICATION_SOURCE_TYPE = "APPLICATION";

const isOrgScope = (scope: SourceType) => scope === "ORGANIZATION";

type SensitivityLevel = "none" | "low" | "moderate" | "high";

export interface RiskItem {
  id: string;
  name: string;
  appCode?: string;
  entityType: string;
  riskCount: number;
  driftCount: number;
  hasChildren: boolean;
  previousFindings?: number;
  sspId?: string;
  sspName?: string;
  totalAssets?: number;
  assetsWithElkData?: number;
  elkDataAvailable?: boolean;
  systemSensitivityLevel: SensitivityLevel;
  /** The application's nearest ORGANIZATION ancestor. Absent for an application
   *  parented straight under a group — such a row still plots, it just has no
   *  organization to be filtered or grouped by. */
  organizationId?: string;
  organizationName?: string;
  /* ── Organization scope only ── */
  /** Drives bubble radius when present; applications fall back to riskCount. */
  sizeValue?: number;
  continuousFailedControlCount?: number;
  /** Applications with a published SSP — the measured subset. */
  applicationCount?: number;
  totalApplicationCount?: number;
  applicationsFailing?: number;
  assetsFailing?: number;
  coverageStatus?: OrgCoverageStatus;
  appCriticality?: string;
  openFindings?: number;
  criticalFindings?: number;
}

interface RiskQuadrantApplication {
  applicationId: string;
  applicationName: string;
  applicationCode?: string;
  /** Nearest ORGANIZATION ancestor; null when the application has none. */
  organizationId?: string | null;
  organizationName?: string | null;
  sspId: string;
  sspName: string;
  totalAssets: number;
  assetsWithElkData: number;
  failedControlCount: number;
  previousFailedControlCount: number;
  driftDelta: number;
  elkDataAvailable: boolean;
  systemSensitivityLevel: SensitivityLevel | string;
}

interface RiskQuadrantData {
  sourceType: string;
  range: string;
  baselineDay: string;
  applicationCount: number;
  applications: RiskQuadrantApplication[];
}

type AppCriticality = "HIGH" | "MODERATE" | "LOW" | string;

interface AppWithoutSsp {
  sourceId: string;
  sourceName: string;
  sourceCode: string;
  criticality: AppCriticality;
  status: string;
  totalAssets: number;
}

interface AppsWithoutSspData {
  sourceType: string;
  count: number;
  items: AppWithoutSsp[];
}

interface FindingsDistributionData {
  sourceType: string;
  applicationCount: number;
  applicationsWithPoam: number;
  totalFindings: number;
  openFindings: number;
  closedFindings: number;
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
}

interface QuadrantSummaryData {
  sourceType: string;
  totalApplications: number;
  applicationsWithSsp: number;
  applicationsWithoutSsp: number;
  sspCoveragePercentage: number;
  openFindings: number;
  criticalFindings: number;
  criticalOpenFindings: number;
  totalAssets: number;
}

interface DashboardEnvelope<T> {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: T;
  pagination?: unknown;
  errors?: unknown;
}

type ControlState = "NEW" | "PERSISTENT" | "RESOLVED";

interface FailingFamily {
  family: string;
  groupName?: string;
  failedControlCount: number;
}

interface FailingControl {
  controlId: string;
  controlTitle?: string;
  controlName?: string;
  family: string;
  failedAssetCount: number;
  state: ControlState;
}

interface WorstAsset {
  assetId: string;
  hostname: string;
  ipAddress: string;
  failedControlCount: number;
}

interface EntityDetailsResponse {
  applicationId: string;
  applicationName: string;
  sspId: string;
  sspName: string;
  applicationCriticality: string;
  systemSensitivityLevel: string;
  range: string;
  baselineDay: string;
  totalAssets: number;
  assetsWithCurrentScan: number;
  assetsWithBaselineScan: number;
  currentFailedControlCount: number;
  previousFailedControlCount: number;
  driftDelta: number;
  newlyFailedControlCount: number;
  persistentFailedControlCount: number;
  newlyResolvedControlCount: number;
  controlImplementationSummary: ControlImplementationSummary;
  findingsSummary: FindingsSummary;
  topFailingFamilies: FailingFamily[];
  topFailingControls: FailingControl[];
  worstAssets: WorstAsset[];
}

interface ControlImplementationSummary {
  totalProfileControls: number;
  claimedControlCount: number;
  missingControlCount: number;
  byImplementationStatus: Record<string, number>;
  byOrigination: Record<string, number>;
  complianceScore?: number;
  applicableControlCount?: number;
  scoreWeights?: Record<string, number>;
  frameworkProfileTitle?: string;
  frameworkCatalogTitle?: string;
}

function complianceScoreColor(pct: number): string {
  if (pct >= 90) return SEV_LOW;
  if (pct >= 70) return SEV_MED;
  if (pct >= 50) return SEV_HIGH;
  return SEV_CRIT;
}

interface FindingsSummary {
  poamId: string;
  totalFindings: number;
  openFindings: number;
  closedFindings: number;
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
}

// Severity palette — aligned with theme chart tokens
const SEV_LOW = "#10b981"; // emerald-500
const SEV_MED = "#f59e0b"; // amber-500
const SEV_HIGH = "#f97316"; // orange-500
const SEV_CRIT = "#ef4444"; // red-500

const ZONE_META: Record<
  ZoneKey,
  {
    color: string;
    label: string;
    icon: typeof Flame;
  }
> = {
  CRITICAL: { color: SEV_CRIT, label: "Critical", icon: Flame },
  "EMERGING RISK": {
    color: SEV_MED,
    label: "Emerging Risk",
    icon: AlertTriangle,
  },
  IMPROVING: { color: SEV_HIGH, label: "Improving", icon: TrendingDown },
  HEALTHY: { color: SEV_LOW, label: "Healthy", icon: ShieldCheck },
};

const SENS_NONE = "#3b82f6"; // blue-500
const SENSITIVITY_COLORS: Record<SensitivityLevel, string> = {
  none: SENS_NONE,
  low: SEV_LOW,
  moderate: SEV_MED,
  high: SEV_CRIT,
};

function normalizeSensitivity(v: unknown): SensitivityLevel {
  const s = String(v ?? "").toLowerCase();
  if (s === "low" || s === "moderate" || s === "high" || s === "none") return s;
  return "none";
}

function getSensitivityColor(level: SensitivityLevel): string {
  return SENSITIVITY_COLORS[level];
}

function getZone(x: number, y: number, splitX: number): ZoneKey {
  if (x < splitX && y >= 0.5) return "EMERGING RISK";
  if (x >= splitX && y >= 0.5) return "CRITICAL";
  if (x < splitX && y < 0.5) return "HEALTHY";
  return "IMPROVING";
}

/* ── Configurable axis metrics ─────────────────────────────────────────── */

export type AxisMetricId =
  | "failedControls"
  | "complianceDrift"
  | "totalAssets"
  | "assetsScanned"
  | "appCriticality"
  | "sensitivityLevel"
  | "openFindings"
  | "criticalFindings"
  /* Organization scope */
  | "repeatFailures"
  | "applications"
  | "applicationsFailing"
  | "assetsFailing";

function criticalityToNumber(level: string | undefined): number {
  const v = String(level ?? "").toUpperCase();
  if (v === "CRITICAL") return 4;
  if (v === "HIGH") return 3;
  if (v === "MEDIUM" || v === "MODERATE") return 2;
  if (v === "LOW") return 1;
  return 0;
}

function sensitivityToNumber(level: SensitivityLevel): number {
  if (level === "high") return 3;
  if (level === "moderate") return 2;
  if (level === "low") return 1;
  return 0;
}

// Distinct label sets — criticality uses 5 buckets (none + 4 levels);
// sensitivity uses 4 buckets (none + 3 levels).
const CRITICALITY_LABELS = ["—", "Low", "Medium", "High", "Critical"];
const SENSITIVITY_LABELS = ["None", "Low", "Moderate", "High"];
const formatCriticality = (v: number) =>
  CRITICALITY_LABELS[Math.round(Math.max(0, Math.min(4, v)))] ?? String(v);
const formatSensitivity = (v: number) =>
  SENSITIVITY_LABELS[Math.round(Math.max(0, Math.min(3, v)))] ?? String(v);

export interface AxisMetric {
  id: AxisMetricId;
  label: string;
  axisTitle: string;
  getValue: (item: RiskItem) => number;
  format: (value: number) => string;
  isCategorical: boolean;
  // For categorical metrics, the highest integer value on the axis.
  // Bipolar metrics can take negative values; axis spans -maxAbs..+maxAbs.
  isBipolar?: boolean;
  categoryMax?: number;
  defaultThreshold: number;
}

const AXIS_METRICS: AxisMetric[] = [
  {
    id: "failedControls",
    label: "Failed Controls",
    axisTitle: "Failed Controls",
    getValue: (i) => i.riskCount,
    format: (v) => String(Math.round(v)),
    isCategorical: false,
    defaultThreshold: 15,
  },
  {
    id: "complianceDrift",
    label: "Compliance Drift",
    axisTitle: "Compliance Drift",
    getValue: (i) => i.driftCount,
    format: (v) => {
      const r = Math.round(v);
      return r > 0 ? `+${r}` : String(r);
    },
    isCategorical: false,
    isBipolar: true,
    defaultThreshold: 0,
  },
  {
    id: "totalAssets",
    label: "Total Assets",
    axisTitle: "Total Assets",
    getValue: (i) => i.totalAssets ?? 0,
    format: (v) => String(Math.round(v)),
    isCategorical: false,
    defaultThreshold: 100,
  },
  {
    id: "assetsScanned",
    label: "Assets Scanned",
    axisTitle: "Assets Scanned",
    getValue: (i) => i.assetsWithElkData ?? 0,
    format: (v) => String(Math.round(v)),
    isCategorical: false,
    defaultThreshold: 50,
  },
  {
    id: "appCriticality",
    label: "App Criticality",
    axisTitle: "App Criticality",
    getValue: (i) => criticalityToNumber(i.appCriticality),
    format: formatCriticality,
    isCategorical: true,
    categoryMax: 4,
    defaultThreshold: 3,
  },
  {
    id: "sensitivityLevel",
    label: "System Sensitivity",
    axisTitle: "System Sensitivity Level",
    getValue: (i) => sensitivityToNumber(i.systemSensitivityLevel),
    format: formatSensitivity,
    isCategorical: true,
    categoryMax: 3,
    defaultThreshold: 2,
  },
  {
    id: "openFindings",
    label: "Open Findings",
    axisTitle: "Open Findings",
    getValue: (i) => i.openFindings ?? 0,
    format: (v) => String(Math.round(v)),
    isCategorical: false,
    defaultThreshold: 10,
  },
  {
    id: "criticalFindings",
    label: "Critical Findings",
    axisTitle: "Critical Findings",
    getValue: (i) => i.criticalFindings ?? 0,
    format: (v) => String(Math.round(v)),
    isCategorical: false,
    defaultThreshold: 3,
  },
];

function computeAxisRange(
  metric: AxisMetric,
  items: RiskItem[],
  threshold?: number,
): { min: number; max: number } {
  if (metric.isBipolar) {
    const maxAbs = Math.max(
      5,
      threshold !== undefined ? Math.abs(threshold) : 0,
      ...items.map((i) => Math.abs(metric.getValue(i))),
    );
    return { min: -maxAbs, max: maxAbs };
  }
  if (metric.isCategorical) {
    return { min: 0, max: metric.categoryMax ?? 3 };
  }
  const dataMax = Math.max(0, ...items.map((i) => metric.getValue(i)));
  if (threshold !== undefined) {
    return {
      min: 0,
      max: Math.max(threshold * 2, Math.ceil(dataMax * 1.4), 30),
    };
  }
  return { min: 0, max: Math.max(dataMax * 1.1, 5) };
}

function computeAxisTicks(
  metric: AxisMetric,
  range: { min: number; max: number },
): number[] {
  if (metric.isCategorical) {
    const ticks: number[] = [];
    for (let v = range.min; v <= range.max; v++) ticks.push(v);
    return ticks;
  }
  return [0, 0.25, 0.5, 0.75, 1].map(
    (t) => range.min + t * (range.max - range.min),
  );
}

/** Metric list for the active scope — organizations have their own axes. */
function axisMetricsFor(scope: SourceType): AxisMetric[] {
  return isOrgScope(scope) ? ORG_AXIS_METRICS : AXIS_METRICS;
}

function getAxisMetric(id: AxisMetricId, metrics: AxisMetric[]): AxisMetric {
  return metrics.find((m) => m.id === id) ?? metrics[0];
}

// Backfills app-level metrics that the current backend doesn't return
// (criticality, open / critical findings per-app). Deterministic per app
// so positions stay stable across renders. Replace with real fields once
// the backend exposes them.
function mockAppMetrics(applicationId: string): {
  appCriticality: string;
  openFindings: number;
  criticalFindings: number;
} {
  const seed = Array.from(applicationId || "app").reduce(
    (acc, ch) => acc + ch.charCodeAt(0),
    0,
  );
  const critLevels = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
  return {
    appCriticality: critLevels[seed % critLevels.length],
    openFindings: (seed * 3) % 60,
    criticalFindings: (seed * 7) % 14,
  };
}

function RiskTooltipCard({
  name,
  appCode,
  xLabel,
  xValue,
  yLabel,
  yValue,
  failedControls,
  sizeLabel = "Failed Controls (size)",
  sizeValue,
  entityNoun = "Application",
  coverageStatus,
  bubbleColor,
  zone,
  onViewApplication,
  onMouseEnter,
  onMouseLeave,
}: {
  name: string;
  appCode?: string;
  xLabel: string;
  xValue: string;
  yLabel: string;
  yValue: string;
  failedControls: number;
  sizeLabel?: string;
  sizeValue?: number;
  entityNoun?: string;
  coverageStatus?: OrgCoverageStatus;
  bubbleColor: string;
  zone: ZoneKey;
  onViewApplication: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const zoneMeta = ZONE_META[zone];
  const encodedValue = sizeValue ?? failedControls;
  // The bubble's radius encodes a metric; show it as a hint unless a selected
  // axis already exposes the same number.
  const sizeMetricLabel = sizeLabel.replace(" (size)", "");
  const showFailedControlsHint =
    xLabel !== sizeMetricLabel && yLabel !== sizeMetricLabel;
  // An organization with applications but no published SSP sits at the origin
  // and reads as healthy. Say so on the card rather than let the colour lie.
  const coverage =
    coverageStatus && coverageStatus !== "COVERED"
      ? orgCoverageMeta(coverageStatus)
      : null;
  return (
    <div
      /* The wrapping foreignObject is pointer-events:none so the card never
         blocks hover on bubbles behind it; the card itself opts back in so
         the View Application action stays clickable. */
      style={{ pointerEvents: "auto" }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="rounded-xl overflow-hidden bg-white dark:bg-[#131320] border border-[#e2e8f0] dark:border-white/[0.08] shadow-[0_1px_2px_rgba(15,23,42,0.06),0_18px_36px_-8px_rgba(15,23,42,0.18),0_0_24px_rgba(15,23,42,0.04)]"
    >
      {/* Header — app name + zone badge */}
      <div className="flex items-start justify-between gap-3 px-4 pt-3.5 pb-2.5">
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-[#0f172a] dark:text-white truncate leading-tight">
            {name}
          </p>
          {appCode && (
            <p className="text-[10px] font-semibold tracking-wider text-[#94a3b8] dark:text-white/50 mt-0.5">
              {appCode}
            </p>
          )}
        </div>
        <span
          className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide whitespace-nowrap"
          style={{
            background: `${zoneMeta.color}1F`,
            color: zoneMeta.color,
            border: `1px solid ${zoneMeta.color}33`,
          }}
        >
          {zoneMeta.label}
        </span>
      </div>

      {/* Metric rows */}
      <div className="px-4 pb-3 space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] text-[#64748b] dark:text-white/55 truncate">
            {xLabel}
            <span className="ml-1.5 text-[9px] font-bold tracking-wider text-[#94a3b8] dark:text-white/40">
              X
            </span>
          </span>
          <span
            className="text-[14px] font-bold tabular-nums"
            style={{ color: bubbleColor }}
          >
            {xValue}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] text-[#64748b] dark:text-white/55 truncate">
            {yLabel}
            <span className="ml-1.5 text-[9px] font-bold tracking-wider text-[#94a3b8] dark:text-white/40">
              Y
            </span>
          </span>
          <span className="text-[14px] font-bold tabular-nums text-[#0f172a] dark:text-white">
            {yValue}
          </span>
        </div>
        {showFailedControlsHint && (
          <div className="flex items-center justify-between gap-3 pt-1.5 border-t border-[#f1f5f9] dark:border-white/[0.06]">
            <span className="text-[11px] text-[#94a3b8] dark:text-white/50">
              {sizeLabel}
            </span>
            <span className="text-[12px] font-semibold tabular-nums text-[#475569] dark:text-white/65">
              {encodedValue}
            </span>
          </div>
        )}
        {coverage && (
          <div
            className="flex items-start gap-2 mt-1.5 pt-1.5 border-t border-[#f1f5f9] dark:border-white/[0.06]"
            title={coverage.hint}
          >
            <AlertCircle
              className="size-3.5 flex-shrink-0 mt-px"
              style={{ color: coverage.color }}
            />
            <span className="text-[11px] leading-snug text-[#64748b] dark:text-white/55">
              <span
                className="font-semibold"
                style={{ color: coverage.color }}
              >
                {coverage.label}
              </span>{" "}
              — {coverage.hint}
            </span>
          </div>
        )}
      </div>

      {/* View link */}
      <div className="px-4 py-2.5 border-t border-[#f1f5f9] dark:border-white/[0.06]">
        <button
          type="button"
          onClick={onViewApplication}
          aria-label={`View ${entityNoun.toLowerCase()} ${name}`}
          className="inline-flex items-center gap-1 text-[12px] font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors cursor-pointer"
        >
          View {entityNoun}
          <ArrowUpRight className="size-3.5 -rotate-45" />
        </button>
      </div>
    </div>
  );
}

/* When the Quadrant view is embedded in the Executive Insights tabs, the parent
   provides a DOM slot (in the tab-bar row) to render the Source/Range filters
   into, so they share the same row as the tabs. Null on the standalone route,
   where the filters render inline in the header. */
export const QuadrantControlsSlotContext = createContext<HTMLElement | null>(null);

export function QuadrantDashboard({ hideTitle = false }: { hideTitle?: boolean } = {}) {
  const controlsSlot = useContext(QuadrantControlsSlotContext);
  const [rangeFilter, setRangeFilter] = useState<RangeFilter>("DAY");
  const [sourceFilter, setSourceFilter] = useState<SourceType>("APPLICATION");
  const [threshold, setThreshold] = useState<number>(DEFAULT_THRESHOLD);
  const [riskItems, setRiskItems] = useState<RiskItem[]>([]);
  const [riskMapLoading, setRiskMapLoading] = useState(false);
  const [riskMapError, setRiskMapError] = useState<string | null>(null);
  const [riskQuadrantData, setRiskQuadrantData] =
    useState<RiskQuadrantData | null>(null);

  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] =
    useState<EntityDetailsResponse | null>(null);
  const [selectedZone, setSelectedZone] = useState<ZoneKey | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const [appsWithoutSsp, setAppsWithoutSsp] = useState<AppWithoutSsp[]>([]);
  const [withoutSspLoading, setWithoutSspLoading] = useState(false);

  const [findingsDist, setFindingsDist] =
    useState<FindingsDistributionData | null>(null);
  const [findingsDistLoading, setFindingsDistLoading] = useState(false);

  const [summary, setSummary] = useState<QuadrantSummaryData | null>(null);

  const [xAxisId, setXAxisId] = useState<AxisMetricId>("appCriticality");
  const [yAxisId, setYAxisId] = useState<AxisMetricId>("assetsScanned");

  /* ── Organization scope ── */
  /* Which groups and organizations the page is narrowed to. Applied — the
     pending copies below are what the open dropdowns are editing, so a request
     only fires when the operator hits Apply rather than on every checkbox. */
  const [scope, setScope] = useState<QuadrantScopeSelection>(EMPTY_SCOPE);
  const [pendingGroupIds, setPendingGroupIds] = useState<string[]>([]);
  const [pendingOrgIds, setPendingOrgIds] = useState<string[]>([]);
  const [scopeOptions, setScopeOptions] = useState<QuadrantScopeOptionsData | null>(null);
  /* Starts true so the scope dropdowns render on the first paint in a loading
     state, rather than popping into existence when the response lands. */
  const [scopeOptionsLoading, setScopeOptionsLoading] = useState(true);
  /* The level the caller holds, from scope-options. It decides what the chart
     plots: a group-level caller gets organization bubbles, everyone below gets
     applications. There is no user-facing override — narrowing is what the
     scope dropdowns do, at whichever level the caller sits. */
  const [scopeLevel, setScopeLevel] = useState<FilterScopeData["level"]>(null);
  /* Which entities are plotted. Empty = all, matching the dropdown's
     "All Organizations" / "All Applications" resting state. */
  const [orgSummary, setOrgSummary] = useState<OrgQuadrantSummaryData | null>(
    null,
  );
  const [orgDrawerOpen, setOrgDrawerOpen] = useState(false);
  const [orgDrillDown, setOrgDrillDown] = useState<OrgDrillDownData | null>(
    null,
  );
  const [orgDrillLoading, setOrgDrillLoading] = useState(false);

  const [firstLoad, setFirstLoad] = useState({
    summary: false,
    risk: false,
    findings: false,
    withoutSsp: false,
  });
  const markFirstLoad = (key: keyof typeof firstLoad) =>
    setFirstLoad((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  const orgScope = isOrgScope(sourceFilter);
  /* The one string every request on this page must append. Deriving it once
     rather than per call site is deliberate: the tiles, the plot, the findings
     donut and the without-SSP panel are read side by side, and a panel that
     forgot to include it would silently report a different estate. */
  const scopeSuffix = scopeQuery(scope);

  const groups = scopeOptions?.groups ?? [];

  // What the caller may filter by. Returns both levels at once, plus the level
  // they hold — which seeds the default view but no longer locks it, since the
  // level toggle can override it either way.
  useEffect(() => {
    let cancelled = false;
    const loadScopeOptions = async () => {
      try {
        const response = (await getAPI(
          API_URL.QUADRANT_SCOPE_OPTIONS,
        )) as DashboardEnvelope<QuadrantScopeOptionsData>;
        if (cancelled) return;
        const data = response?.data ?? null;
        setScopeOptions(data);
        setScopeLevel(data?.level ?? null);
        setSourceFilter(data?.level === "GROUP" ? "ORGANIZATION" : "APPLICATION");
        /* A caller who holds exactly one group has no choice to make, so every
           request is scoped to it and the chip names it. Seeded into the
           pending copy too, so opening the dropdown shows it already ticked
           instead of looking unfiltered while the data says otherwise. */
        const onlyGroup = data?.groups?.length === 1 ? data.groups[0].id : null;
        if (onlyGroup) {
          setScope({ groupIds: [onlyGroup], orgIds: [] });
          setPendingGroupIds([onlyGroup]);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load quadrant scope options:", error);
        setScopeOptions(null);
      } finally {
        if (!cancelled) setScopeOptionsLoading(false);
      }
    };
    void loadScopeOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  // Axis ids belong to the active scope's metric list — carrying an
  // application axis (say Compliance Drift) into the organization view would
  // resolve to the fallback metric and silently plot the wrong number.
  const axisMetrics = useMemo(
    () => axisMetricsFor(sourceFilter),
    [sourceFilter],
  );

  useEffect(() => {
    if (orgScope) {
      setXAxisId("failedControls");
      setYAxisId("repeatFailures");
    } else {
      // The organization list has neither of these, which is why the two
      // scopes reset to different pairs.
      setXAxisId("appCriticality");
      setYAxisId("assetsScanned");
    }
  }, [orgScope]);

  useEffect(() => {
    let cancelled = false;

    if (scopeOptionsLoading) return;

    // Not applicable in the organization view — the panel is hidden there.
    if (orgScope) {
      setAppsWithoutSsp([]);
      markFirstLoad("withoutSsp");
      return;
    }

    const loadWithoutSsp = async () => {
      setWithoutSspLoading(true);
      try {
        const response = (await getAPI(
          `${API_URL.QUADRANT_WITHOUT_SSP}?source_type=${sourceFilter}${scopeSuffix}`,
        )) as DashboardEnvelope<AppsWithoutSspData>;

        if (cancelled) return;

        if (response.status === "success" && response.data) {
          setAppsWithoutSsp(response.data.items ?? []);
        } else {
          setAppsWithoutSsp([]);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load applications without SSP:", error);
        setAppsWithoutSsp([]);
      } finally {
        if (!cancelled) {
          setWithoutSspLoading(false);
          markFirstLoad("withoutSsp");
        }
      }
    };

    void loadWithoutSsp();

    return () => {
      cancelled = true;
    };
  }, [sourceFilter, orgScope, scopeSuffix, scopeOptionsLoading]);

  // KPI tiles. The two scopes have different tile sets — organizations replace
  // the application count — so they are separate endpoints, not one shape with
  // nullable halves. Both take the same scope, so the tiles count the estate
  // the chart plots rather than staying tenant-wide beside a filtered chart.
  useEffect(() => {
    let cancelled = false;

    if (scopeOptionsLoading) return;

    const loadSummary = async () => {
      try {
        const url = orgScope
          ? `${API_URL.QUADRANT_ORG_SUMMARY}${scopeQueryFirst(scope)}`
          : `${API_URL.QUADRANT_SUMMARY}?source_type=${sourceFilter}${scopeSuffix}`;
        const response = (await getAPI(url)) as DashboardEnvelope<
          QuadrantSummaryData | OrgQuadrantSummaryData
        >;

        if (cancelled) return;

        const ok = response.status === "success" && response.data;
        if (orgScope) {
          setOrgSummary(ok ? (response.data as OrgQuadrantSummaryData) : null);
          setSummary(null);
        } else {
          setSummary(ok ? (response.data as QuadrantSummaryData) : null);
          setOrgSummary(null);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load quadrant summary:", error);
        setSummary(null);
        setOrgSummary(null);
      } finally {
        if (!cancelled) markFirstLoad("summary");
      }
    };

    void loadSummary();

    return () => {
      cancelled = true;
    };
  }, [sourceFilter, orgScope, scope, scopeSuffix, scopeOptionsLoading]);

  useEffect(() => {
    let cancelled = false;

    if (scopeOptionsLoading) return;

    const loadFindingsDist = async () => {
      setFindingsDistLoading(true);
      try {
        // Findings always roll up from application POA&Ms, at either level —
        // the scope narrows the population so this panel counts the same
        // estate as the tiles beside it.
        const response = (await getAPI(
          `${API_URL.QUADRANT_FINDINGS_DISTRIBUTION}?source_type=${APPLICATION_SOURCE_TYPE}${scopeSuffix}`,
        )) as DashboardEnvelope<FindingsDistributionData>;

        if (cancelled) return;

        if (response.status === "success" && response.data) {
          setFindingsDist(response.data);
        } else {
          setFindingsDist(null);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load findings distribution:", error);
        setFindingsDist(null);
      } finally {
        if (!cancelled) {
          setFindingsDistLoading(false);
          markFirstLoad("findings");
        }
      }
    };

    void loadFindingsDist();

    return () => {
      cancelled = true;
    };
  }, [sourceFilter, scopeSuffix, scopeOptionsLoading]);

  useEffect(() => {
    let cancelled = false;

    if (scopeOptionsLoading) return;

    const normalizeRiskItem = (
      application: RiskQuadrantApplication,
    ): RiskItem => {
      const mock = mockAppMetrics(application.applicationId);
      return {
        id: `${application.applicationId}:${application.sspId}`,
        name:
          application.applicationName || application.sspName || "Application",
        appCode: application.applicationCode || undefined,
        entityType: "Application",
        riskCount: application.failedControlCount ?? 0,
        driftCount: application.driftDelta ?? 0,
        hasChildren: false,
        previousFindings: application.previousFailedControlCount ?? 0,
        sspId: application.sspId,
        sspName: application.sspName,
        totalAssets: application.totalAssets ?? 0,
        assetsWithElkData: application.assetsWithElkData ?? 0,
        elkDataAvailable: application.elkDataAvailable ?? false,
        systemSensitivityLevel: normalizeSensitivity(
          application.systemSensitivityLevel,
        ),
        organizationId: application.organizationId ?? undefined,
        organizationName: application.organizationName ?? undefined,
        appCriticality: mock.appCriticality,
        openFindings: mock.openFindings,
        criticalFindings: mock.criticalFindings,
      };
    };

    const loadRiskQuadrant = async () => {
      setRiskMapLoading(true);
      setRiskMapError(null);
      try {
        const url = orgScope
          ? `${API_URL.QUADRANT_ORGANIZATIONS}?range=${rangeFilter}${scopeSuffix}`
          : `${API_URL.QUADRANT_RISK_QUADRANT}?source_type=${sourceFilter}&range=${rangeFilter}${scopeSuffix}`;
        const response = (await getAPI(url)) as DashboardEnvelope<
          RiskQuadrantData | OrgQuadrantData
        >;

        if (cancelled) return;

        if (response.status !== "success" || !response.data) {
          throw new Error(
            "Risk quadrant API returned an unsuccessful response.",
          );
        }

        if (orgScope) {
          const data = response.data as OrgQuadrantData;
          setRiskQuadrantData(null);
          setRiskItems((data.organizations ?? []).map(normalizeOrgRow));
        } else {
          const data = response.data as RiskQuadrantData;
          setRiskQuadrantData(data);
          setRiskItems((data.applications ?? []).map(normalizeRiskItem));
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load risk quadrant data:", error);
        setRiskQuadrantData(null);
        setRiskItems([]);
        setRiskMapError(
          orgScope
            ? "Unable to load organization risk quadrant data."
            : "Unable to load application risk quadrant data.",
        );
      } finally {
        if (!cancelled) {
          setRiskMapLoading(false);
          markFirstLoad("risk");
        }
      }
    };

    void loadRiskQuadrant();

    return () => {
      cancelled = true;
    };
  }, [rangeFilter, sourceFilter, orgScope, scopeSuffix, scopeOptionsLoading]);


  const handleViewOrgDetails = async (orgId: string) => {
    setOrgDrillLoading(true);
    setOrgDrawerOpen(true);
    setOrgDrillDown(null);
    try {
      const response = (await getAPI(
        `${API_URL.QUADRANT_ORGANIZATIONS}/${orgId}/drill-down?range=${rangeFilter}`,
      )) as DashboardEnvelope<OrgDrillDownData>;
      if (response.status === "success" && response.data) {
        setOrgDrillDown(response.data);
      } else {
        setOrgDrawerOpen(false);
      }
    } catch (err) {
      console.error("Failed to load organization details:", err);
      setOrgDrawerOpen(false);
    } finally {
      setOrgDrillLoading(false);
    }
  };

  const openApplicationDetails = async (
    entityId: string,
    zone: ZoneKey | null,
    fallbackName?: string,
  ) => {
    setDetailsLoading(true);
    setDetailDrawerOpen(true);
    setSelectedZone(zone);
    setDetailsError(null);

    /* Only meaningful when the plot is already showing this application. Opened
       from the organization drawer's Applications list the plot holds
       ORGANISATIONS, so there is no row to fall back to and every field would
       be a fabricated zero — see the guard on the call sites below. */
    const plottedItem = riskItems.find((r) => r.id === entityId);

    const buildFallback = (): EntityDetailsResponse => {
      const item = plottedItem;
      const [appId, sspId] = entityId.split(":");
      return {
        applicationId: appId ?? entityId,
        applicationName: fallbackName ?? item?.name ?? "Unknown Application",
        sspId: sspId ?? item?.sspId ?? "",
        sspName: item?.sspName ?? "",
        applicationCriticality: "",
        systemSensitivityLevel: "",
        range: rangeFilter,
        baselineDay: riskQuadrantData?.baselineDay ?? "",
        totalAssets: item?.totalAssets ?? 0,
        assetsWithCurrentScan: item?.assetsWithElkData ?? 0,
        assetsWithBaselineScan: 0,
        currentFailedControlCount: item?.riskCount ?? 0,
        previousFailedControlCount: item?.previousFindings ?? 0,
        driftDelta: item?.driftCount ?? 0,
        newlyFailedControlCount: 0,
        persistentFailedControlCount: 0,
        newlyResolvedControlCount: 0,
        controlImplementationSummary: {
          totalProfileControls: 0,
          claimedControlCount: 0,
          missingControlCount: 0,
          byImplementationStatus: {},
          byOrigination: {},
        },
        findingsSummary: {
          poamId: "",
          totalFindings: 0,
          openFindings: 0,
          closedFindings: 0,
          byStatus: {},
          bySeverity: {},
        },
        topFailingFamilies: [],
        topFailingControls: [],
        worstAssets: [],
      };
    };

    try {
      const [applicationId] = entityId.split(":");
      const response = (await getAPI(
        `${API_URL.QUADRANT_ENTITY_DETAILS}/${applicationId}/drill-down?source_type=APPLICATION&range=${rangeFilter}`,
      )) as DashboardEnvelope<EntityDetailsResponse>;

      if (response.status === "success" && response.data) {
        setSelectedEntity(response.data);
      } else if (plottedItem) {
        setSelectedEntity(buildFallback());
      } else {
        setSelectedEntity(null);
        setDetailsError(
          `No drill-down data was returned for ${fallbackName ?? "this application"}.`,
        );
      }
    } catch (err) {
      console.error("Failed to load entity details:", err);
      if (plottedItem) {
        setSelectedEntity(buildFallback());
      } else {
        setSelectedEntity(null);
        const msg = err instanceof Error ? err.message : "";
        setDetailsError(
          msg === "OFFLINE"
            ? "You appear to be offline."
            : `Could not load details for ${fallbackName ?? "this application"}.`,
        );
      }
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewDetails = async (entityId: string, zone: ZoneKey) => {
    if (orgScope) {
      await handleViewOrgDetails(entityId);
      return;
    }
    await openApplicationDetails(entityId, zone);
  };

  const xMetric = useMemo(
    () => getAxisMetric(xAxisId, axisMetrics),
    [xAxisId, axisMetrics],
  );
  const yMetric = useMemo(
    () => getAxisMetric(yAxisId, axisMetrics),
    [yAxisId, axisMetrics],
  );

  /* Everything the page draws comes straight from the response now. Narrowing
     is the scope dropdowns' job — they re-query rather than filter locally, so
     the tiles and the chart can never disagree about what is in scope. */
  const visibleItems = riskItems;

  const xAxisRange = useMemo(
    () => computeAxisRange(xMetric, visibleItems, threshold),
    [xMetric, visibleItems, threshold],
  );
  const yAxisRange = useMemo(
    () => computeAxisRange(yMetric, visibleItems),
    [yMetric, visibleItems],
  );

  // Reset threshold to the metric's natural default when the X axis changes
  // so the threshold line stays inside the chart range.
  useEffect(() => {
    setThreshold(xMetric.defaultThreshold);
  }, [xMetric]);

  const criticalZoneCount = useMemo(
    () =>
      visibleItems.filter(
        (i) =>
          xMetric.getValue(i) >= threshold &&
          yMetric.getValue(i) >= (yAxisRange.min + yAxisRange.max) / 2,
      ).length,
    [visibleItems, threshold, xMetric, yMetric, yAxisRange],
  );

  const totalAssets = useMemo(
    () => visibleItems.reduce((acc, i) => acc + (i.totalAssets ?? 0), 0),
    [visibleItems],
  );

  /* Ranked worst-first once, then sliced three ways: the plot takes the top
     ten so the bubbles stay legible, the side panel takes thirty, and the
     View More drawer takes everything. */
  const rankedItems = useMemo(
    () =>
      [...visibleItems].sort((a, b) => {
        if (b.riskCount !== a.riskCount) return b.riskCount - a.riskCount;
        return b.driftCount - a.driftCount;
      }),
    [visibleItems],
  );

  const plottedItems = useMemo(
    () => rankedItems.slice(0, QUADRANT_PLOT_TOP_N),
    [rankedItems],
  );

  const topCritical = useMemo(() => rankedItems.slice(0, 30), [rankedItems]);

  /* Organizations offered for selection, narrowed by whichever groups are
     pending. Picking a group then opening this list should not still offer the
     organizations of every other group — that reads as the group filter having
     done nothing. With no group picked, every organization is offered. */
  const orgOptions = useMemo(() => {
    const all = scopeOptions?.organizations ?? [];
    const matching =
      pendingGroupIds.length === 0
        ? all
        : all.filter((o) => o.parentId && pendingGroupIds.includes(o.parentId));
    /* scope-options does not always populate `parentId` — when it is absent the
       match above drops every row and the dropdown disappears entirely, which
       reads as "this group has no organizations". The response is already
       scoped to what the caller may see, so falling back to the full list is
       the honest answer rather than showing nothing. */
    const withinGroups = matching.length > 0 ? matching : all;
    return withinGroups
      .map((o) => ({
        value: o.id,
        label:
          o.applicationCount > 0 ? `${o.name} (${o.applicationCount})` : o.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [scopeOptions, pendingGroupIds]);

  const groupOptions = useMemo(
    () =>
      groups
        .map((g) => ({
          value: g.id,
          label:
            g.applicationCount > 0 ? `${g.name} (${g.applicationCount})` : g.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [groups],
  );

  /* Named only when the whole page sits inside exactly one group. More than one
     group has none to name, and labelling the view after one of several would
     describe a subset of what is on screen. Narrowing to organizations does not
     clear it: an org selection now replaces the group in the query rather than
     stacking on it, so the chip stops being a claim about what was requested and
     is simply the group the viewer sits in. Dropping it there would also make the
     control row jump, since the group dropdown only replaces it when there is
     more than one group to choose between. */
  const groupName = useMemo(() => {
    if (scope.groupIds.length !== 1) return null;
    return groups.find((g) => g.id === scope.groupIds[0])?.name ?? null;
  }, [scope, groups]);

  const applyScope = (groupIds: string[], orgIds: string[]) => {
    setScope({ groupIds, orgIds });
  };

  const initialLoading =
    scopeOptionsLoading ||
    !firstLoad.summary ||
    !firstLoad.risk ||
    !firstLoad.findings ||
    !firstLoad.withoutSsp;

  return (
    <div className="space-y-4">
      {/* ── Page header ── */}
      {/* Source/Range filters. When embedded (a slot is provided), they render
          into the parent's tab-bar row via a portal; otherwise inline here. */}
      {(() => {
        const controls = (
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            {/* The group name arrives with scope-options, so the chip holds its
                slot from the first paint with a spinner in it. Letting it
                appear late shifts every control to its right just as the reader
                is reaching for one. */}
            {scopeOptionsLoading ? (
              <span className="inline-flex h-[38px] min-w-[120px] items-center justify-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-3 text-sm font-semibold text-indigo-700 dark:border-indigo-400/40 dark:bg-indigo-500/10 dark:text-indigo-300">
                <Loader2 className="size-3.5 animate-spin" />
                Loading
              </span>
            ) : /* A single group is context rather than a choice, so it reads as
                  a label; more than one and it becomes a real filter. */
            groupName ? (
              <span className="inline-flex h-[38px] items-center rounded-lg border border-indigo-300 bg-indigo-50 px-3 text-sm font-semibold text-indigo-700 dark:border-indigo-400/40 dark:bg-indigo-500/10 dark:text-indigo-300">
                {groupName}
              </span>
            ) : (
              groupOptions.length > 1 && (
                <MultiSelectDropdown
                  placeholder="All Groups"
                  selectedNoun="Group"
                  options={groupOptions}
                  selected={pendingGroupIds}
                  onChange={setPendingGroupIds}
                  onApply={() => applyScope(pendingGroupIds, pendingOrgIds)}
                  onClearAll={() => {
                    setPendingGroupIds([]);
                    applyScope([], pendingOrgIds);
                  }}
                />
              )
            )}
            {/* Rendered while scope-options is still in flight, not just once it
                lands — a control that appears late reads as the page changing
                shape under the reader. It carries its own spinner instead. */}
            {(scopeOptionsLoading || orgOptions.length > 0) && (
              <MultiSelectDropdown
                loading={scopeOptionsLoading}
                placeholder="All Organizations"
                selectedNoun="Organization"
                options={orgOptions}
                selected={pendingOrgIds}
                onChange={setPendingOrgIds}
                onApply={() => applyScope(pendingGroupIds, pendingOrgIds)}
                onClearAll={() => {
                  setPendingOrgIds([]);
                  applyScope(pendingGroupIds, []);
                }}
              />
            )}
            <RangeFilter value={rangeFilter} onChange={setRangeFilter} />
          </div>
        );
        if (hideTitle && controlsSlot) return createPortal(controls, controlsSlot);
        return (
          <div className={hideTitle ? "" : "border-b border-[#e2e8f0] dark:border-white/[0.06] pb-4"}>
            <div className="flex items-center justify-between gap-6 flex-wrap">
              {hideTitle ? (
                <div />
              ) : (
                <div className="min-w-0">
                  <h1 className="text-2xl font-bold tracking-tight text-[#0f172a] dark:text-white">
                    Executive Risk Quadrant
                  </h1>
                  <p className="mt-0.5 text-sm text-[#475569] dark:text-white/55">
                    Risk &amp; Compliance Posture
                  </p>
                </div>
              )}
              {controls}
            </div>
          </div>
        );
      })()}

      {/* ── Body ── */}
      {initialLoading || riskMapLoading ? (
        <PanelLoading />
      ) : riskMapError ? (
        <PanelError title="Unable to Load Risk Posture" detail={riskMapError} />
      ) : visibleItems.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-[#131320] px-6 py-16 text-center">
          <Inbox className="size-5 text-[#94a3b8] dark:text-white/30 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#0f172a] dark:text-white">
            No Risk Data Available
          </p>
          <p className="mt-1 text-xs text-[#64748b] dark:text-white/45">
            Adjust the scope or time range to view historical posture
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <KpiStrip
            scope={sourceFilter}
            applicationCount={
              orgScope
                ? (orgSummary?.totalOrganizations ?? visibleItems.length)
                : (summary?.totalApplications ??
                  riskQuadrantData?.applicationCount ??
                  visibleItems.length)
            }
            sspCoveragePercentage={
              (orgScope ? orgSummary : summary)?.sspCoveragePercentage ?? 0
            }
            totalApplications={
              (orgScope ? orgSummary : summary)?.totalApplications ?? 0
            }
            applicationsWithSsp={
              (orgScope ? orgSummary : summary)?.applicationsWithSsp ?? 0
            }
            applicationsWithoutSsp={
              (orgScope ? orgSummary : summary)?.applicationsWithoutSsp ??
              (orgScope ? 0 : appsWithoutSsp.length)
            }
            openFindings={
              (orgScope ? orgSummary : summary)?.openFindings ??
              findingsDist?.openFindings ??
              0
            }
            criticalFindings={
              (orgScope ? orgSummary : summary)?.criticalFindings ??
              findingsDist?.bySeverity?.critical ??
              0
            }
            criticalOpenFindings={
              (orgScope ? orgSummary : summary)?.criticalOpenFindings ?? 0
            }
            criticalZoneCount={criticalZoneCount}
            totalAssets={
              (orgScope ? orgSummary : summary)?.totalAssets ?? totalAssets
            }
          />
          <QuadrantView
            scope={sourceFilter}
            metrics={axisMetrics}
            items={plottedItems}
            allItems={rankedItems}
            topCritical={topCritical}
            xAxisRange={xAxisRange}
            yAxisRange={yAxisRange}
            xMetric={xMetric}
            yMetric={yMetric}
            xAxisId={xAxisId}
            yAxisId={yAxisId}
            onXAxisChange={setXAxisId}
            onYAxisChange={setYAxisId}
            threshold={threshold}
            appsWithoutSsp={appsWithoutSsp}
            withoutSspLoading={withoutSspLoading}
            findingsDist={findingsDist}
            findingsDistLoading={findingsDistLoading}
            onThresholdChange={setThreshold}
            onViewDetails={handleViewDetails}
          />
        </div>
      )}

      {detailDrawerOpen && (
        <EntityDetailsDrawer
          entity={selectedEntity}
          zone={selectedZone}
          open={detailDrawerOpen}
          onClose={() => {
            setDetailDrawerOpen(false);
            setSelectedEntity(null);
            setSelectedZone(null);
            setDetailsError(null);
          }}
          loading={detailsLoading}
          error={detailsError}
          zIndexClass={orgDrawerOpen ? "z-[60]" : "z-50"}
        />
      )}

      {orgDrawerOpen && (
        <OrgDetailsDrawer
          data={orgDrillDown}
          open={orgDrawerOpen}
          loading={orgDrillLoading}
          onClose={() => {
            setOrgDrawerOpen(false);
            setOrgDrillDown(null);
          }}
          onSelectApplication={(application) =>
            void openApplicationDetails(
              application.applicationId,
              null,
              application.applicationName,
            )
          }
        />
      )}
    </div>
  );
}

/* ── Loading / error panels ────────────────────────────────────────────── */

function PanelLoading() {
  return (
    <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] px-6 py-16 text-center shadow-sm">
      <Loader2 className="size-5 animate-spin text-indigo-500 mx-auto mb-3" />
      <p className="text-sm font-semibold text-[#0f172a] dark:text-white">
        Loading Risk Posture
      </p>
      <p className="text-xs text-[#64748b] dark:text-white/45 mt-1">
        Aggregating control compliance signals
      </p>
    </div>
  );
}

function PanelError({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="rounded-xl border border-red-200/60 dark:border-red-500/20 bg-red-50/50 dark:bg-red-500/[0.06] px-6 py-12 text-center">
      <AlertCircle className="size-5 text-red-500 mx-auto mb-3" />
      <p className="text-sm font-semibold text-red-700 dark:text-red-300">
        {title}
      </p>
      <p className="text-xs text-red-600/80 dark:text-red-300/70 mt-1 max-w-md mx-auto">
        {detail}
      </p>
    </div>
  );
}

/* ── Level Filter ──────────────────────────────────────────────────────── */

/**
 * Which entity the chart plots — organizations or the applications inside them.
 *
 * <p>Rendered only for a caller who holds a GROUP. Everyone below that level has
 * exactly one level available to them, so the control would have a single
 * working position. It is a level switch rather than a scope filter: narrowing
 * to particular organizations is what the scope dropdowns beside it do, and
 * either level can be narrowed the same way.
 */
function LevelFilterControl({
  value,
  onChange,
}: {
  value: SourceType;
  onChange: (v: SourceType) => void;
}) {
  const active =
    SOURCE_OPTIONS.find((o) => o.id === value) ?? SOURCE_OPTIONS[0];
  return (
    <div className="inline-flex items-stretch rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-[#131320] overflow-hidden shadow-sm">
      <div className="inline-flex items-center gap-2 px-3 border-r border-[#e2e8f0] dark:border-white/[0.08] bg-[#f8fafc] dark:bg-white/[0.02]">
        <Building2 className="size-4 text-[#64748b] dark:text-white/55" />
        <span className="text-xs font-semibold text-[#64748b] dark:text-white/55">
          Plot
        </span>
      </div>
      <div className="relative">
        <select
          aria-label="Level plotted"
          value={value}
          onChange={(e) => onChange(e.target.value as SourceType)}
          className="appearance-none bg-transparent pl-3.5 pr-8 py-2 text-xs font-semibold text-[#0f172a] dark:text-white focus:outline-none cursor-pointer"
        >
          {SOURCE_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id} className="text-[#0f172a]">
              {opt.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 size-3 text-[#64748b] dark:text-white/55"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {/* SR-only label fallback */}
        <span className="sr-only">{active.label}</span>
      </div>
    </div>
  );
}

/* ── Range Filter ──────────────────────────────────────────────────────── */

function RangeFilter({
  value,
  onChange,
}: {
  value: RangeFilter;
  onChange: (v: RangeFilter) => void;
}) {
  /* No leading-label chip: the pickers beside this read "All Organizations",
     not "Scope: All Organizations", and the chip was the only thing making this
     control a different shape. Width is left to the content — the scope pickers
     need 190px for names like "3 Organizations Selected", but the longest value
     here is "Month", and padding a five-letter word out to that width is just
     whitespace. */
  const opts: { id: RangeFilter; label: string }[] = [
    { id: "DAY", label: "Day" },
    { id: "WEEK", label: "Week" },
    { id: "MONTH", label: "Month" },
    { id: "YEAR", label: "Year" },
  ];
  return (
    <Dropdown
      value={value}
      onChange={(v) => onChange(v as RangeFilter)}
      options={opts.map(({ id, label }) => ({ value: id, label }))}
      size="sm"
      /* `!` because the trigger hardcodes min-w-[160px] in its base classes —
         same specificity otherwise, so which one wins depends on stylesheet
         order. The codebase already overrides this width the same way. */
      className="!min-w-[92px]"
      align="end"
    />
  );
}

/* ── Axis Picker ───────────────────────────────────────────────────────── */

function AxisPicker({
  axis,
  value,
  onChange,
  disabledId,
  metrics,
}: {
  axis: "X" | "Y";
  value: AxisMetricId;
  onChange: (id: AxisMetricId) => void;
  disabledId?: AxisMetricId;
  metrics: AxisMetric[];
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const current = getAxisMetric(value, metrics);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${axis} axis metric: ${current.label}`}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-stretch rounded-lg border bg-white dark:bg-[#131320] overflow-hidden shadow-sm transition-colors ${
          open
            ? "border-indigo-500 dark:border-indigo-400 ring-2 ring-indigo-500/15"
            : "border-[#e2e8f0] dark:border-white/[0.08] hover:border-[#cbd5e1] dark:hover:border-white/[0.14]"
        }`}
      >
        <span className="inline-flex items-center gap-1.5 px-2.5 border-r border-[#e2e8f0] dark:border-white/[0.08] bg-[#f8fafc] dark:bg-white/[0.02]">
          <span className="inline-flex items-center justify-center size-4 rounded-md text-[10px] font-bold bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-300">
            {axis}
          </span>
          <span className="text-[11px] font-semibold text-[#64748b] dark:text-white/55">
            Axis
          </span>
        </span>
        <span className="inline-flex items-center pl-2.5 pr-2 py-1.5 text-[11px] font-semibold text-[#0f172a] dark:text-white gap-1.5">
          <span className="whitespace-nowrap">{current.label}</span>
          <svg
            className={`size-3 text-[#64748b] dark:text-white/55 transition-transform ${
              open ? "rotate-180" : ""
            }`}
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden
          >
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={`${axis} axis metric options`}
          className="absolute z-30 top-full mt-1.5 left-0 min-w-full w-[220px] rounded-xl overflow-hidden bg-white dark:bg-[#131320] border border-[#e2e8f0] dark:border-white/[0.08]"
          style={{
            boxShadow:
              "0 1px 2px rgba(15,23,42,0.06), 0 18px 36px -8px rgba(15,23,42,0.18), 0 0 24px rgba(15,23,42,0.04)",
          }}
        >
          <div className="px-3 pt-2.5 pb-1.5 border-b border-[#f1f5f9] dark:border-white/[0.06] flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center size-4 rounded-md text-[10px] font-bold bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-300">
              {axis}
            </span>
            <span className="text-[10px] font-bold tracking-[0.14em] text-[#94a3b8] dark:text-white/45">
              {axis === "X" ? "X-axis Metric" : "Y-axis Metric"}
            </span>
          </div>
          <ul className="py-1 max-h-72 overflow-y-auto">
            {metrics.map((m) => {
              const isActive = m.id === value;
              const isDisabled = m.id === disabledId;
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      onChange(m.id);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-1.5 text-[12px] font-semibold transition-colors text-left ${
                      isDisabled
                        ? "text-[#cbd5e1] dark:text-white/25 cursor-not-allowed"
                        : isActive
                          ? "bg-indigo-50 dark:bg-indigo-500/[0.12] text-indigo-700 dark:text-indigo-200"
                          : "text-[#0f172a] dark:text-white hover:bg-[#f8fafc] dark:hover:bg-white/[0.04]"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2 truncate">
                      <span
                        className={`size-1.5 rounded-full flex-shrink-0 ${
                          isActive
                            ? "bg-indigo-600 dark:bg-indigo-400"
                            : isDisabled
                              ? "bg-[#e2e8f0] dark:bg-white/15"
                              : "bg-[#cbd5e1] dark:bg-white/25"
                        }`}
                      />
                      <span className="truncate">{m.label}</span>
                    </span>
                    {isDisabled && (
                      <span className="text-[9px] font-bold tracking-wider text-[#94a3b8] dark:text-white/40 whitespace-nowrap flex-shrink-0">
                        on other axis
                      </span>
                    )}
                    {isActive && !isDisabled && (
                      <svg
                        className="size-3 text-indigo-600 dark:text-indigo-400 flex-shrink-0"
                        viewBox="0 0 12 12"
                        fill="none"
                        aria-hidden
                      >
                        <path
                          d="M2.5 6.5L5 9L9.5 3.5"
                          stroke="currentColor"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ── Threshold Control ─────────────────────────────────────────────────── */

function ThresholdControl({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const clamp = (n: number) =>
    Math.max(THRESHOLD_MIN, Math.min(THRESHOLD_MAX, n));
  return (
    <div className="inline-flex items-center gap-2">
      <span className="text-xs font-semibold text-[#64748b] dark:text-white/55">
        Threshold
      </span>
      <div className="inline-flex items-stretch rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-[#131320] overflow-hidden shadow-sm">
        <button
          type="button"
          onClick={() => onChange(clamp(value - 1))}
          disabled={value <= THRESHOLD_MIN}
          className="px-2 text-[#475569] dark:text-white/65 hover:bg-[#f8fafc] dark:hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Decrease threshold"
        >
          <Minus className="size-3.5" />
        </button>
        <input
          type="text"
          inputMode="numeric"
          pattern="\d*"
          value={value}
          onChange={(e) => {
            const raw = e.target.value.replace(/\D/g, "");
            if (raw === "") return;
            const v = parseInt(raw, 10);
            if (!Number.isNaN(v)) onChange(clamp(v));
          }}
          className="w-10 text-center bg-transparent border-x border-[#e2e8f0] dark:border-white/[0.08] py-1 text-sm font-semibold tabular-nums text-[#0f172a] dark:text-white focus:outline-none focus:bg-[#f8fafc] dark:focus:bg-white/[0.04]"
          aria-label="Findings threshold"
        />
        <button
          type="button"
          onClick={() => onChange(clamp(value + 1))}
          disabled={value >= THRESHOLD_MAX}
          className="px-2 text-[#475569] dark:text-white/65 hover:bg-[#f8fafc] dark:hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Increase threshold"
        >
          <Plus className="size-3.5" />
        </button>
      </div>
      {value !== DEFAULT_THRESHOLD && (
        <button
          type="button"
          onClick={() => onChange(DEFAULT_THRESHOLD)}
          className="inline-flex items-center gap-1 text-xs font-medium text-[#64748b] dark:text-white/55 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          aria-label="Reset threshold"
          title={`Reset to ${DEFAULT_THRESHOLD}`}
        >
          <RotateCcw className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/* ── Applications without SSP Panel ────────────────────────────────────── */

function getCriticalityColor(criticality: AppCriticality): string {
  const c = String(criticality).toUpperCase();
  if (c === "HIGH") return SEV_CRIT;
  if (c === "MODERATE" || c === "MEDIUM") return SEV_MED;
  if (c === "LOW") return "#3b82f6"; // blue-500 — distinct from green "healthy"
  return "#94a3b8";
}

function AppsWithoutSspPanel({
  apps,
  loading,
}: {
  apps: AppWithoutSsp[];
  loading: boolean;
}) {
  return (
    <div
      id="applications-without-ssp-panel"
      data-name="Applications without SSP"
      className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.06)] dark:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] flex flex-col"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06]">
        <div className="inline-flex items-center gap-2">
          <FileWarning className="size-4 text-amber-500" />
          <p className="text-sm font-semibold text-[#0f172a] dark:text-white">
            Applications without SSP
          </p>
        </div>
        <span className="inline-flex items-center justify-center min-w-6 h-5 px-1.5 rounded-md bg-amber-50 dark:bg-amber-500/[0.12] text-xs font-semibold tabular-nums text-amber-700 dark:text-amber-300">
          {apps.length}
        </span>
      </div>

      {loading ? (
        <div className="px-4 py-6 text-center">
          <Loader2 className="size-4 animate-spin text-indigo-500 mx-auto" />
        </div>
      ) : apps.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <ShieldCheck className="size-5 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-[#0f172a] dark:text-white">
            Fully Authorized
          </p>
          <p className="text-xs text-[#64748b] dark:text-white/45 mt-1">
            Every application has an authorized SSP
          </p>
        </div>
      ) : (
        <>
          {/* Column header */}
          <div className="grid grid-cols-[1fr_56px_72px] gap-4 items-center px-4 py-1.5 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] text-[10px] font-semibold tracking-wider text-[#94a3b8] dark:text-white/45">
            <span>Application</span>
            <span className="text-right">Assets</span>
            <span className="text-right">Criticality</span>
          </div>
          <ul className="divide-y divide-[#f1f5f9] dark:divide-white/[0.04] h-[170px] overflow-y-auto">
            {apps.map((app) => {
              const color = getCriticalityColor(app.criticality);
              const critLabel =
                String(app.criticality).charAt(0).toUpperCase() +
                String(app.criticality).slice(1).toLowerCase();
              return (
                <li
                  key={app.sourceId}
                  className="grid grid-cols-[1fr_56px_72px] gap-4 items-center px-4 py-2"
                >
                  <p className="text-[12px] font-medium text-[#0f172a] dark:text-white truncate">
                    {app.sourceName}
                  </p>
                  <span
                    className="text-[13px] font-bold tabular-nums leading-none text-[#0f172a] dark:text-white text-right"
                    title={`${app.totalAssets} assets`}
                  >
                    {app.totalAssets}
                  </span>
                  <span
                    className="text-[12px] font-semibold leading-none text-right"
                    style={{ color }}
                  >
                    {critLabel}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

/* ── Findings Distribution Panel ───────────────────────────────────────── */

function FindingsDistributionPanel({
  data,
  loading,
}: {
  data: FindingsDistributionData | null;
  loading: boolean;
}) {
  // Severity order, colour, and label are fixed by the domain, so the slices are
  // handed to the chart pre-coloured and unsorted rather than taking the
  // component's palette and descending sort.
  const severitySlices = SEVERITY_ORDER.map((s) => ({
    name: s.key === "unspecified" ? "No Severity" : s.label,
    value: data?.bySeverity?.[s.key] ?? 0,
    color: s.color,
  }));
  const severityTotal = severitySlices.reduce((acc, s) => acc + s.value, 0);

  return (
    <div
      id="findings-by-severity-chart"
      data-name="Findings by Severity"
      className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.06)] dark:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] flex flex-col"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06]">
        <div className="inline-flex items-center gap-2">
          <Activity className="size-4 text-indigo-500" />
          <p className="text-sm font-semibold text-[#0f172a] dark:text-white">
            Findings by Severity
          </p>
        </div>
      </div>

      {!loading && (!data || severityTotal === 0) ? (
        <div className="px-4 py-8 text-center">
          <ShieldCheck className="size-5 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-[#0f172a] dark:text-white">
            No Findings
          </p>
        </div>
      ) : (
        <div className="px-4 py-3">
          <DonutPieChart
            id="findings-by-severity-donut"
            name="Findings by Severity"
            data={severitySlices}
            loading={loading}
            // Taller than it was on purpose: leader labels are drawn outside the arc and a crowded
            // tail spreads vertically, which the previous 150 did not leave room for.
            height={240}
            sort="none"
            maxSlices={SEVERITY_ORDER.length}
            centerLabel="Total"
            legendPosition="right"
            showDataTable={false}
            // Every severity carries its percentage on the chart. Inside-slice labels would drop
            // anything under 8%, which is exactly the low-count severities a reader looks for.
            showSliceLabels
            labelStyle="leader"
            ariaLabel="Donut chart: findings by severity"
          />
        </div>
      )}
    </div>
  );
}

/* ── KPI Strip ─────────────────────────────────────────────────────────── */

function KpiCard({
  label,
  value,
  icon: Icon,
  accent,
  caption,
  hint,
  progress,
}: {
  label: string;
  value: number | string;
  icon: typeof Flame;
  accent: string;
  caption?: string;
  hint?: string;
  progress?: { value: number; total: number };
}) {
  const filledPct =
    progress && progress.total > 0
      ? Math.min(100, Math.max(0, (progress.value / progress.total) * 100))
      : 0;
  return (
    <div
      id={labelToDomId(label, "kpi")}
      data-name={label}
      className="group relative rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] px-3 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_-8px_rgba(15,23,42,0.12)] dark:hover:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 hover:border-[#cbd5e1] dark:hover:border-white/[0.12] transition-all duration-200 overflow-hidden"
    >
      {/* Top accent gradient hairline */}
      <div
        className="absolute top-0 inset-x-0 h-px opacity-70"
        style={{
          background: `linear-gradient(to right, transparent, ${accent}, transparent)`,
        }}
      />
      {/* Soft corner glow */}
      <div
        className="absolute -top-6 -right-6 size-20 rounded-full opacity-[0.10] blur-2xl pointer-events-none group-hover:opacity-[0.18] transition-opacity duration-200"
        style={{ background: accent }}
      />
      <div className="relative flex items-start justify-between gap-2">
        <p className="text-[9.5px] font-bold tracking-[0.14em] text-[#64748b] dark:text-white/55 leading-tight">
          {label}
        </p>
        <span
          className="inline-flex items-center justify-center size-6 rounded-full border flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
          style={{
            background: `${accent}14`,
            borderColor: `${accent}33`,
            color: accent,
            boxShadow: `inset 0 0 0 1px ${accent}10`,
          }}
        >
          <Icon className="size-3" />
        </span>
      </div>
      <p
        className="relative mt-1 text-[23px] font-bold tabular-nums leading-none tracking-tight text-[#0f172a] dark:text-white"
        style={{ fontFeatureSettings: '"ss01", "cv11"' }}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      {caption && (
        <p className="relative mt-0.5 text-[9.5px] font-semibold text-[#64748b] dark:text-white/60 truncate">
          {caption}
        </p>
      )}
      {progress && (
        <div className="relative mt-1.5 flex items-center gap-1.5">
          <span className="h-[3px] flex-1 overflow-hidden rounded-full bg-[#e2e8f0] dark:bg-white/[0.12]">
            <span
              className="block h-full rounded-full transition-[width] duration-500"
              style={{ width: `${filledPct}%`, background: accent }}
            />
          </span>
          <span className="text-[9px] font-bold tabular-nums whitespace-nowrap text-[#64748b] dark:text-white/55">
            {progress.value.toLocaleString()}/{progress.total.toLocaleString()}
          </span>
        </div>
      )}
      {hint && (
        <p className="relative mt-0.5 text-[9.5px] font-medium text-[#94a3b8] dark:text-white/50 truncate">
          {hint}
        </p>
      )}
    </div>
  );
}

function KpiStrip({
  scope,
  applicationCount,
  sspCoveragePercentage,
  totalApplications,
  applicationsWithSsp,
  applicationsWithoutSsp,
  openFindings,
  criticalFindings,
  criticalOpenFindings,
  criticalZoneCount,
  totalAssets,
}: {
  scope: SourceType;
  applicationCount: number;
  sspCoveragePercentage: number;
  totalApplications: number;
  applicationsWithSsp: number;
  applicationsWithoutSsp: number;
  openFindings: number;
  criticalFindings: number;
  criticalOpenFindings: number;
  criticalZoneCount: number;
  totalAssets: number;
}) {
  const sspPct = Number.isFinite(sspCoveragePercentage)
    ? sspCoveragePercentage
    : 0;
  const sspPctLabel = Number.isInteger(sspPct)
    ? String(sspPct)
    : sspPct.toFixed(2);
  const sspAccent = sspPct >= 90 ? SEV_LOW : sspPct >= 70 ? SEV_MED : SEV_CRIT;
  const sspIcon =
    sspPct >= 90 ? ShieldCheck : sspPct >= 70 ? ShieldAlert : Shield;
  const criticalHint =
    criticalOpenFindings === 0
      ? "No Critical Open Finding"
      : `${criticalOpenFindings} Open`;
  const orgScope = isOrgScope(scope);
  // The entity tile counts organizations in the org view, but SSP coverage
  // stays application-denominated: counting organizations that hold at least
  // one SSP would call a 40-application org "covered" on the strength of one
  // document, and would climb toward 100% as the real gap widened.
  const entityLabel = orgScope ? "Organizations" : "Applications";
  const acrossHint = orgScope
    ? "Across All Organizations"
    : "Across All Applications";
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <KpiCard
        label={entityLabel}
        value={applicationCount}
        icon={orgScope ? Building2 : AppWindow}
        accent="#4f46e5"
      />
      {/* <KpiCard
        label="SSP Coverage"
        value={`${sspPctLabel}%`}
        icon={sspIcon}
        accent={sspAccent}
        caption="Coverage Achieved"
        progress={{ value: applicationsWithSsp, total: totalApplications }}
        hint={`${applicationsWithoutSsp.toLocaleString()} Applications Uncovered`}
      /> */}
      <KpiCard
        label="Open Findings"
        value={openFindings}
        icon={Activity}
        accent={SEV_HIGH}
        hint={acrossHint}
      />
      <KpiCard
        label="Critical Findings"
        value={criticalFindings}
        icon={Flame}
        accent={SEV_CRIT}
        hint={criticalHint}
      />
      <KpiCard
        label="Critical Zone"
        value={criticalZoneCount}
        icon={AlertTriangle}
        accent={SEV_CRIT}
        hint={orgScope ? "Orgs at Risk" : "Apps at Risk"}
      />
      <KpiCard
        label="Assets"
        value={totalAssets}
        icon={Server}
        accent="#0ea5e9"
        hint={acrossHint}
      />
    </div>
  );
}

/* ── Quadrant View ─────────────────────────────────────────────────────── */

function QuadrantView({
  items,
  allItems,
  topCritical,
  xAxisRange,
  yAxisRange,
  xMetric,
  yMetric,
  xAxisId,
  yAxisId,
  onXAxisChange,
  onYAxisChange,
  threshold,
  appsWithoutSsp,
  withoutSspLoading,
  findingsDist,
  findingsDistLoading,
  onThresholdChange,
  onViewDetails,
  scope,
  metrics,
}: {
  /** What the plot draws — capped at QUADRANT_PLOT_TOP_N. */
  items: RiskItem[];
  /** Every entity in scope, ranked worst-first. Backs the View More drawer, so
   *  the plot's cap never hides a row from the reader entirely. */
  allItems: RiskItem[];
  topCritical: RiskItem[];
  xAxisRange: { min: number; max: number };
  yAxisRange: { min: number; max: number };
  xMetric: AxisMetric;
  yMetric: AxisMetric;
  xAxisId: AxisMetricId;
  yAxisId: AxisMetricId;
  onXAxisChange: (id: AxisMetricId) => void;
  onYAxisChange: (id: AxisMetricId) => void;
  threshold: number;
  appsWithoutSsp: AppWithoutSsp[];
  withoutSspLoading: boolean;
  findingsDist: FindingsDistributionData | null;
  findingsDistLoading: boolean;
  onThresholdChange: (v: number) => void;
  onViewDetails: (entityId: string, zone: ZoneKey) => void;
  scope: SourceType;
  metrics: AxisMetric[];
}) {
  const orgScope = isOrgScope(scope);
  const entityNoun = orgScope ? "Organization" : "Application";
  const entityNounPlural = orgScope ? "Organizations" : "Applications";
  /** Short plural for the fixed-width cluster pill ("+7 orgs"). */
  const entityNounShort = orgScope ? "orgs" : "apps";
  /** Bubble size: total applications for an org, failed controls for an app. */
  const sizeOf = (item: RiskItem) => item.sizeValue ?? item.riskCount;
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoveredClusterId, setHoveredClusterId] = useState<string | null>(null);
  const [frozenXRange, setFrozenXRange] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [allEntitiesOpen, setAllEntitiesOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const svgRef = useRef<SVGSVGElement>(null);
  const clusterCloseTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const bubbleCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClusterEnter = (id: string) => {
    const existing = clusterCloseTimers.current.get(id);
    if (existing) {
      clearTimeout(existing);
      clusterCloseTimers.current.delete(id);
    }
    setHoveredClusterId(id);
  };

  const handleClusterLeave = (id: string) => {
    const t = setTimeout(() => {
      setHoveredClusterId((cur) => (cur === id ? null : cur));
      clusterCloseTimers.current.delete(id);
    }, 140);
    clusterCloseTimers.current.set(id, t);
  };

  // The tooltip sits in a gap next to the bubble, so closing it the instant
  // the pointer leaves the bubble makes "View Application" unreachable.
  // Hold it open briefly; entering the card cancels the close.
  const handleBubbleEnter = (id: string) => {
    if (bubbleCloseTimer.current) {
      clearTimeout(bubbleCloseTimer.current);
      bubbleCloseTimer.current = null;
    }
    setHoveredId(id);
  };

  const handleBubbleLeave = (id: string) => {
    if (bubbleCloseTimer.current) clearTimeout(bubbleCloseTimer.current);
    bubbleCloseTimer.current = setTimeout(() => {
      setHoveredId((cur) => (cur === id ? null : cur));
      bubbleCloseTimer.current = null;
    }, 220);
  };

  // Aspect ratio chosen to roughly match a wide chart-card container so the
  // SVG's preserveAspectRatio="meet" does not letterbox empty side bands.
  const plotWidth = 1150;
  const plotHeight = 700;
  const padding = 40;
  const totalW = plotWidth + padding * 2;
  const totalH = plotHeight + padding * 2;

  // x-axis range — stable during drag so bubbles don't shift.
  // frozenXRange (if set) stores the live max captured at drag-start.
  const liveXMin = xAxisRange.min;
  const liveXMax = xAxisRange.max;
  const xMin = liveXMin;
  const xMax = frozenXRange ?? liveXMax;
  const xSpan = xMax - xMin || 1;
  const splitX = (threshold - xMin) / xSpan;
  const splitPlotX = padding + splitX * plotWidth;

  const yMin = yAxisRange.min;
  const yMax = yAxisRange.max;
  const ySpan = yMax - yMin || 1;

  const getXPosition = (xValue: number): number =>
    Math.max(0.02, Math.min(0.98, (xValue - xMin) / xSpan));

  const getYPosition = (yValue: number): number =>
    Math.max(0.06, Math.min(0.94, (yValue - yMin) / ySpan));

  // Always-visible labels: top 6 by riskCount
  const labeledIds = new Set(topCritical.slice(0, 6).map((i) => i.id));

  // Cluster bubbles whose label boxes would visually collide so we can swap
  // the overlapping names for a single "+N apps"/"+N orgs" pill at the centroid.
  type ClusteredItemPos = {
    item: RiskItem;
    xNorm: number;
    yNorm: number;
    plotX: number;
    plotY: number;
    radius: number;
  };

  const itemPositions: ClusteredItemPos[] = items.map((item) => {
    const xNorm = getXPosition(xMetric.getValue(item));
    const yNorm = getYPosition(yMetric.getValue(item));
    return {
      item,
      xNorm,
      yNorm,
      plotX: padding + xNorm * plotWidth,
      plotY: padding + (1 - yNorm) * plotHeight,
      // Bubble size is a stable visual cue across axis swaps — the chosen axes
      // describe position. Applications encode failed controls; organizations
      // encode total applications, so an un-assessed org keeps its weight.
      radius: Math.max(
        10,
        Math.min(32, Math.ceil(Math.sqrt(sizeOf(item) + 1) * 4.5)),
      ),
    };
  });

  const clusterParent: Record<string, string> = {};
  itemPositions.forEach((p) => {
    clusterParent[p.item.id] = p.item.id;
  });
  const findClusterRoot = (id: string): string => {
    let cur = id;
    while (clusterParent[cur] !== cur) {
      clusterParent[cur] = clusterParent[clusterParent[cur]];
      cur = clusterParent[cur];
    }
    return cur;
  };
  const uniteClusters = (a: string, b: string) => {
    const ra = findClusterRoot(a);
    const rb = findClusterRoot(b);
    if (ra !== rb) clusterParent[ra] = rb;
  };
  for (let i = 0; i < itemPositions.length; i++) {
    for (let j = i + 1; j < itemPositions.length; j++) {
      const a = itemPositions[i];
      const b = itemPositions[j];
      const dx = a.plotX - b.plotX;
      const dy = a.plotY - b.plotY;
      // Cluster when bubbles touch/overlap, or their label boxes below
      // would collide (~30px label-space buffer).
      if (Math.sqrt(dx * dx + dy * dy) < a.radius + b.radius + 30) {
        uniteClusters(a.item.id, b.item.id);
      }
    }
  }
  const clusterMembers = new Map<string, ClusteredItemPos[]>();
  itemPositions.forEach((p) => {
    const root = findClusterRoot(p.item.id);
    const arr = clusterMembers.get(root);
    if (arr) arr.push(p);
    else clusterMembers.set(root, [p]);
  });
  const clusteredIds = new Set<string>();
  const multiClusters: {
    id: string;
    members: ClusteredItemPos[];
    centroidX: number;
    bottomY: number;
  }[] = [];
  clusterMembers.forEach((members, rootId) => {
    if (members.length >= 2) {
      members.forEach((m) => clusteredIds.add(m.item.id));
      const cx = members.reduce((s, m) => s + m.plotX, 0) / members.length;
      const by = Math.max(...members.map((m) => m.plotY + m.radius));
      multiClusters.push({ id: rootId, members, centroidX: cx, bottomY: by });
    }
  });

  const eventToThreshold = (clientX: number, currentXMax: number): number => {
    const svg = svgRef.current;
    if (!svg) return threshold;
    const rect = svg.getBoundingClientRect();
    const viewBoxX = ((clientX - rect.left) / rect.width) * totalW;
    const norm = (viewBoxX - padding) / plotWidth;
    const clamped = Math.max(0, Math.min(1, norm));
    const raw = xMin + clamped * (currentXMax - xMin);
    const v = Math.round(raw);
    return Math.max(xMin, Math.min(currentXMax, v));
  };

  const handlePointerDown = (e: ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFrozenXRange(liveXMax);
    setIsDragging(true);
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: ReactPointerEvent) => {
    if (!isDragging) return;
    const next = eventToThreshold(e.clientX, frozenXRange ?? liveXMax);
    if (next !== threshold) onThresholdChange(next);
  };

  const handlePointerUp = (e: ReactPointerEvent) => {
    if (!isDragging) return;
    setIsDragging(false);
    setFrozenXRange(null);
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex gap-3 flex-col lg:flex-row">
      {/* Chart card */}
      <div
        id="risk-compliance-quadrant-chart"
        data-name="Risk &amp; Compliance Quadrant"
        className="flex-1 lg:max-h-[726px] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.06)] dark:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] flex flex-col min-h-0"
      >
        {/* Chart header strip */}
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[#e2e8f0] dark:border-white/[0.06] flex-wrap">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#0f172a] dark:text-white">
              Risk &amp; Compliance Quadrant
            </p>
            <p className="text-[11px] text-[#64748b] dark:text-white/45 mt-0.5">
              Adjust the risk threshold to redefine Low and High Risk zones
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
            <AxisPicker
              axis="X"
              value={xAxisId}
              onChange={onXAxisChange}
              disabledId={yAxisId}
              metrics={metrics}
            />
            <AxisPicker
              axis="Y"
              value={yAxisId}
              onChange={onYAxisChange}
              disabledId={xAxisId}
              metrics={metrics}
            />
            <div className="hidden lg:flex items-center gap-3 pl-3 ml-1 border-l border-[#e2e8f0] dark:border-white/[0.06]">
              {(
                [
                  { key: "HEALTHY", color: SEV_LOW },
                  { key: "EMERGING RISK", color: SEV_MED },
                  { key: "IMPROVING", color: SEV_HIGH },
                  { key: "CRITICAL", color: SEV_CRIT },
                ] as { key: ZoneKey; color: string }[]
              ).map(({ key, color }) => (
                <span key={key} className="inline-flex items-center gap-1.5">
                  <span
                    className="size-2 rounded-full"
                    style={{ background: color }}
                  />
                  <span className="text-[11px] font-medium text-[#475569] dark:text-white/70">
                    {ZONE_META[key].label}
                  </span>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Chart area — fits viewport, no scroll */}
        <div className="p-1.5 flex-1 min-h-0 flex flex-col">
          <div className="w-full flex-1 min-h-0">
            <svg
              ref={svgRef}
              className="w-full h-full"
              viewBox={`0 0 ${totalW} ${totalH}`}
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Corner zone tints — subtle radial glows fading to transparent at center */}
              <defs>
                <radialGradient id="zone-tl" cx="0%" cy="0%" r="65%">
                  <stop offset="0%" stopColor={SEV_MED} stopOpacity="0.07" />
                  <stop offset="100%" stopColor={SEV_MED} stopOpacity="0" />
                </radialGradient>
                <radialGradient id="zone-tr" cx="100%" cy="0%" r="65%">
                  <stop offset="0%" stopColor={SEV_CRIT} stopOpacity="0.08" />
                  <stop offset="100%" stopColor={SEV_CRIT} stopOpacity="0" />
                </radialGradient>
                <radialGradient id="zone-bl" cx="0%" cy="100%" r="65%">
                  <stop offset="0%" stopColor={SEV_LOW} stopOpacity="0.06" />
                  <stop offset="100%" stopColor={SEV_LOW} stopOpacity="0" />
                </radialGradient>
                <radialGradient id="zone-br" cx="100%" cy="100%" r="65%">
                  <stop offset="0%" stopColor={SEV_HIGH} stopOpacity="0.05" />
                  <stop offset="100%" stopColor={SEV_HIGH} stopOpacity="0" />
                </radialGradient>
              </defs>
              {(["zone-tl", "zone-tr", "zone-bl", "zone-br"] as const).map(
                (id) => (
                  <rect
                    key={id}
                    x={padding}
                    y={padding}
                    width={plotWidth}
                    height={plotHeight}
                    fill={`url(#${id})`}
                  />
                ),
              )}

              {/* Grid lines — subtle, dashed, graph-style */}
              {[0.25, 0.5, 0.75].map((t) => (
                <line
                  key={`vx-${t}`}
                  x1={padding + t * plotWidth}
                  y1={padding}
                  x2={padding + t * plotWidth}
                  y2={padding + plotHeight}
                  stroke="#94a3b8"
                  strokeOpacity="0.18"
                  strokeWidth="1"
                  strokeDasharray="2 4"
                />
              ))}
              {[0.25, 0.5, 0.75].map((t) => (
                <line
                  key={`hy-${t}`}
                  x1={padding}
                  y1={padding + t * plotHeight}
                  x2={padding + plotWidth}
                  y2={padding + t * plotHeight}
                  stroke="#94a3b8"
                  strokeOpacity="0.18"
                  strokeWidth="1"
                  strokeDasharray="2 4"
                />
              ))}

              {/* Axis spines — only left + bottom, like a graph */}
              <line
                x1={padding}
                y1={padding}
                x2={padding}
                y2={padding + plotHeight}
                stroke="#cbd5e1"
                strokeWidth="1"
              />
              <line
                x1={padding}
                y1={padding + plotHeight}
                x2={padding + plotWidth}
                y2={padding + plotHeight}
                stroke="#cbd5e1"
                strokeWidth="1"
              />

              {/* X-axis ticks — categorical metrics tick at each integer
                  category so users see every level (Low/Medium/High/...). */}
              {computeAxisTicks(xMetric, { min: xMin, max: xMax }).map(
                (rawValue, idx) => {
                  const px = padding + ((rawValue - xMin) / xSpan) * plotWidth;
                  const display = xMetric.format(rawValue);
                  return (
                    <g key={`xt-${idx}`}>
                      <line
                        x1={px}
                        y1={padding + plotHeight}
                        x2={px}
                        y2={padding + plotHeight + 4}
                        stroke="#94a3b8"
                        strokeWidth="1"
                      />
                      <text
                        x={px}
                        y={padding + plotHeight + 16}
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight="600"
                        className="fill-[#334155] dark:fill-white/80"
                      >
                        {display}
                      </text>
                    </g>
                  );
                },
              )}

              {/* Y-axis ticks */}
              {computeAxisTicks(yMetric, { min: yMin, max: yMax }).map(
                (rawValue, idx) => {
                  const py =
                    padding + (1 - (rawValue - yMin) / ySpan) * plotHeight;
                  const display = yMetric.format(rawValue);
                  return (
                    <g key={`yt-${idx}`}>
                      <line
                        x1={padding - 4}
                        y1={py}
                        x2={padding}
                        y2={py}
                        stroke="#94a3b8"
                        strokeWidth="1"
                      />
                      <text
                        x={padding - 7}
                        y={py + 4}
                        textAnchor="end"
                        fontSize="11"
                        fontWeight="600"
                        className="fill-[#334155] dark:fill-white/80"
                      >
                        {display}
                      </text>
                    </g>
                  );
                },
              )}

              {/* Corner annotations — zone labels per quadrant */}
              <text
                x={padding + 6}
                y={padding + 14}
                fontSize="10"
                fontWeight="700"
                letterSpacing="1.5"
                className="fill-[#334155] dark:fill-white"
              >
                EMERGING RISK
              </text>
              <text
                x={padding + plotWidth - 6}
                y={padding + 14}
                textAnchor="end"
                fontSize="10"
                fontWeight="700"
                letterSpacing="1.5"
                className="fill-[#334155] dark:fill-white"
              >
                CRITICAL
              </text>
              <text
                x={padding + 6}
                y={padding + plotHeight - 6}
                fontSize="10"
                fontWeight="700"
                letterSpacing="1.5"
                className="fill-[#334155] dark:fill-white"
              >
                HEALTHY
              </text>
              <text
                x={padding + plotWidth - 6}
                y={padding + plotHeight - 6}
                textAnchor="end"
                fontSize="10"
                fontWeight="700"
                letterSpacing="1.5"
                className="fill-[#334155] dark:fill-white"
              >
                IMPROVING
              </text>

              {/* Horizontal drift midline */}
              <line
                x1={padding}
                y1={padding + plotHeight / 2}
                x2={padding + plotWidth}
                y2={padding + plotHeight / 2}
                stroke="#cbd5e1"
                strokeWidth="1"
                strokeDasharray="3 3"
              />
              {/* Threshold (vertical) line — draggable */}
              <line
                x1={splitPlotX}
                y1={padding}
                x2={splitPlotX}
                y2={padding + plotHeight}
                className={
                  isDragging
                    ? "stroke-indigo-600"
                    : "stroke-[#94a3b8] dark:stroke-white/40"
                }
                strokeWidth={isDragging ? "2" : "1.5"}
                strokeDasharray={isDragging ? "0" : "4 3"}
                style={{ transition: "stroke 0.12s" }}
              />

              {/* Draggable threshold handle — scale arrow above the chart */}
              <g
                style={{ cursor: "ew-resize", touchAction: "none" }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenuPos({ x: e.clientX, y: e.clientY });
                }}
              >
                <title>{`Failed-controls threshold: ${threshold} · drag to adjust`}</title>
                {/* Wide invisible hit area covering bookmark + line */}
                <rect
                  x={splitPlotX - 18}
                  y={padding - 36}
                  width={36}
                  height={plotHeight + 38}
                  fill="transparent"
                />
                {/* Bookmark marker — ribbon hanging over the line */}
                <g
                  transform={`translate(${splitPlotX}, ${padding})`}
                  className={
                    isDragging
                      ? "text-indigo-600"
                      : "text-[#0f172a] dark:text-white"
                  }
                >
                  <path
                    d="M -14 -32 L 14 -32 L 14 -8 L 0 0 L -14 -8 Z"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth={1}
                    strokeLinejoin="round"
                    style={{
                      filter:
                        "drop-shadow(0 2px 4px rgba(15, 23, 42, 0.25)) drop-shadow(0 4px 8px rgba(15, 23, 42, 0.15))",
                      transition: "color 0.12s",
                    }}
                  />
                  <text
                    x={0}
                    y={-16}
                    textAnchor="middle"
                    fontSize="13"
                    fontWeight="700"
                    className={
                      isDragging
                        ? "fill-white"
                        : "fill-white dark:fill-[#0f172a]"
                    }
                  >
                    {threshold}
                  </text>
                </g>
              </g>

              {/* Axis labels */}
              <text
                x={padding + plotWidth / 2}
                y={padding + plotHeight + 38}
                textAnchor="middle"
                fontSize="15"
                fontWeight="700"
                className="fill-[#1e293b] dark:fill-white/90"
              >
                {xMetric.label} →
              </text>
              <text
                x={16}
                y={padding + plotHeight / 2}
                textAnchor="middle"
                fontSize="15"
                fontWeight="700"
                className="fill-[#1e293b] dark:fill-white/90"
                transform={`rotate(-90 16 ${padding + plotHeight / 2})`}
              >
                ↑ {yMetric.label}
              </text>

              {/* Bubbles */}
              {items.map((item) => {
                const x = getXPosition(xMetric.getValue(item));
                const y = getYPosition(yMetric.getValue(item));
                const zone = getZone(x, y, splitX);
                const plotX = padding + x * plotWidth;
                const plotY = padding + (1 - y) * plotHeight;
                const radius = Math.max(
                  10,
                  Math.min(32, Math.ceil(Math.sqrt(sizeOf(item) + 1) * 4.5)),
                );
                const isHovered = hoveredId === item.id;
                const bubbleColor = ZONE_META[zone].color;
                const showLabel =
                  labeledIds.has(item.id) && !clusteredIds.has(item.id);

                return (
                  <g key={item.id}>
                    {/* Critical zone: gently pulsing halo */}
                    {zone === "CRITICAL" && (
                      <circle
                        cx={plotX}
                        cy={plotY}
                        r={radius + 5}
                        fill={bubbleColor}
                        fillOpacity="0.22"
                        pointerEvents="none"
                      >
                        <animate
                          attributeName="r"
                          values={`${radius + 3};${radius + 10};${radius + 3}`}
                          dur="2.6s"
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="fill-opacity"
                          values="0.28;0.06;0.28"
                          dur="2.6s"
                          repeatCount="indefinite"
                        />
                      </circle>
                    )}
                    {/* subtle hover ring */}
                    {isHovered && (
                      <circle
                        cx={plotX}
                        cy={plotY}
                        r={radius + 4}
                        fill="none"
                        stroke={bubbleColor}
                        strokeWidth="1.5"
                        strokeOpacity="0.35"
                      />
                    )}
                    {/* Outline carries the colour; the interior is a dulled
                        wash of the same hue so the ring reads as the shape and
                        the fill never competes with it. `r` is inset by half
                        the stroke so the drawn edge lands on the true radius
                        rather than straddling it. */}
                    <circle
                      cx={plotX}
                      cy={plotY}
                      r={Math.max(1, radius - 1.25)}
                      fill={bubbleColor}
                      fillOpacity={isHovered ? 0.3 : 0.16}
                      stroke={bubbleColor}
                      strokeWidth={isHovered ? 3 : 2.5}
                      strokeOpacity={isHovered ? 1 : 0.9}
                      style={{
                        cursor: item.riskCount > 0 ? "pointer" : "default",
                        transition:
                          "fill-opacity 0.18s ease, stroke-width 0.18s ease, stroke-opacity 0.18s ease",
                        filter: isHovered
                          ? `drop-shadow(0 0 10px ${bubbleColor}80)`
                          : `drop-shadow(0 0 5px ${bubbleColor}40)`,
                      }}
                      onMouseEnter={() => handleBubbleEnter(item.id)}
                      onMouseLeave={() => handleBubbleLeave(item.id)}
                      onClick={() => {
                        if (item.riskCount > 0) onViewDetails(item.id, zone);
                      }}
                    >
                      {/* Slow breathe on the outline only. Paused on hover so
                          the ring is a steady target while being clicked, and
                          skipped entirely for reduced-motion readers. */}
                      {!isHovered && !prefersReducedMotion && (
                        <animate
                          attributeName="stroke-opacity"
                          values="0.9;0.45;0.9"
                          dur="3.2s"
                          repeatCount="indefinite"
                        />
                      )}
                    </circle>
                    {/* Always-on label for top critical bubbles. Prefer the
                       short applicationCode when present so the label stays
                       compact under the bubble; fall back to a truncated name. */}
                    {showLabel && !isHovered && (
                      <g pointerEvents="none">
                        <text
                          x={plotX}
                          y={plotY + radius + 16}
                          textAnchor="middle"
                          fontSize="13"
                          fontWeight="600"
                          className="fill-[#0f172a] dark:fill-white"
                        >
                          {item.appCode
                            ? item.appCode.length > 14
                              ? item.appCode.slice(0, 13) + "…"
                              : item.appCode
                            : item.name.length > 22
                              ? item.name.slice(0, 21) + "…"
                              : item.name}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}

              {/* Cluster pills: replace overlapping labels with a count
                 + hover popover that lists the underlying entities. Hovering
                 a row sets hoveredId, which triggers the existing
                 RiskTooltipCard render path next to that bubble. */}
              {multiClusters.map((cluster) => {
                const isClusterHovered = hoveredClusterId === cluster.id;
                const pillH = 22;
                // Pill is sized to fit its text; popover is wider but
                // absolutely positioned so the pill stays anchored to
                // the cluster centroid even near the chart edges.
                const pillW = 70;
                const popW = 240;
                const headerH = 32;
                const rowH = 30;
                const popH = Math.min(
                  220,
                  headerH + Math.min(cluster.members.length, 6) * rowH,
                );

                const pillX = Math.max(
                  padding + 4,
                  Math.min(
                    padding + plotWidth - pillW - 4,
                    cluster.centroidX - pillW / 2,
                  ),
                );
                const pillY = cluster.bottomY + 8;
                const pillCenter = pillX + pillW / 2;

                const spaceBelow = padding + plotHeight - (pillY + pillH);
                const openBelow = spaceBelow >= popH + 8;

                // Popover ideally centers on the pill but clamps into
                // the chart so it never bleeds off the left/right edge.
                const desiredPopLeft = pillCenter - popW / 2;
                const popLeftAbs = Math.max(
                  padding + 4,
                  Math.min(padding + plotWidth - popW - 4, desiredPopLeft),
                );
                const popLeftRel = popLeftAbs - pillX;

                return (
                  <foreignObject
                    key={`cluster-${cluster.id}`}
                    x={pillX}
                    y={pillY}
                    width={pillW}
                    height={pillH + 2}
                    style={{ overflow: "visible", pointerEvents: "none" }}
                  >
                    <div
                      style={{
                        position: "relative",
                        width: pillW,
                        pointerEvents: "none",
                      }}
                    >
                      <div
                        style={{
                          height: pillH,
                          lineHeight: `${pillH}px`,
                          pointerEvents: "auto",
                        }}
                        className="text-center rounded-full bg-[#0f172a] dark:bg-white text-white dark:text-[#0f172a] text-[11px] font-bold cursor-pointer shadow-md ring-1 ring-white/20 dark:ring-[#0f172a]/10 select-none whitespace-nowrap"
                        onMouseEnter={() => handleClusterEnter(cluster.id)}
                        onMouseLeave={() => handleClusterLeave(cluster.id)}
                      >
                        +{cluster.members.length} {entityNounShort}
                      </div>
                      {isClusterHovered && (
                        <div
                          style={{
                            position: "absolute",
                            top: openBelow ? pillH + 8 : -(popH + 8),
                            left: popLeftRel,
                            width: popW,
                            pointerEvents: "auto",
                            boxShadow:
                              "0 1px 2px rgba(15,23,42,0.06), 0 18px 36px -8px rgba(15,23,42,0.18), 0 0 24px rgba(15,23,42,0.04)",
                          }}
                          className="rounded-xl overflow-hidden bg-white dark:bg-[#131320] border border-[#e2e8f0] dark:border-white/[0.08]"
                          onMouseEnter={() => handleClusterEnter(cluster.id)}
                          onMouseLeave={() => handleClusterLeave(cluster.id)}
                        >
                          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-[#f1f5f9] dark:border-white/[0.06]">
                            <p className="text-[10px] font-bold tracking-[0.18em] text-[#94a3b8] dark:text-white/45">
                              {entityNounPlural} here
                            </p>
                            <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-md bg-indigo-50 dark:bg-indigo-500/[0.12] text-[10px] font-bold tabular-nums text-indigo-600 dark:text-indigo-300">
                              {cluster.members.length}
                            </span>
                          </div>
                          <div
                            className="overflow-y-auto py-1"
                            style={{ maxHeight: 180 }}
                          >
                            {cluster.members.map((m) => {
                              const zoneKey = getZone(m.xNorm, m.yNorm, splitX);
                              const color = ZONE_META[zoneKey].color;
                              const zoneLabel = ZONE_META[zoneKey].label;
                              const isRowHovered = hoveredId === m.item.id;
                              return (
                                <button
                                  key={m.item.id}
                                  type="button"
                                  className={`w-full flex items-center gap-2 px-4 py-1.5 text-left transition-colors ${isRowHovered ? "bg-[#f8fafc] dark:bg-white/[0.05]" : "hover:bg-[#f8fafc] dark:hover:bg-white/[0.03]"}`}
                                  style={{
                                    cursor:
                                      m.item.riskCount > 0
                                        ? "pointer"
                                        : "default",
                                  }}
                                  onMouseEnter={() => handleBubbleEnter(m.item.id)}
                                  onMouseLeave={() => handleBubbleLeave(m.item.id)}
                                  onClick={() => {
                                    if (m.item.riskCount > 0)
                                      onViewDetails(m.item.id, zoneKey);
                                  }}
                                  title={zoneLabel}
                                >
                                  <span
                                    className="size-2 rounded-full flex-shrink-0"
                                    style={{
                                      background: color,
                                      boxShadow: `0 0 0 2px ${color}26`,
                                    }}
                                  />
                                  <span className="text-[12px] font-medium text-[#0f172a] dark:text-white truncate flex-1">
                                    {m.item.name}
                                  </span>
                                  <span
                                    className="text-[12px] font-bold tabular-nums"
                                    style={{ color }}
                                  >
                                    {m.item.riskCount}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </foreignObject>
                );
              })}

              {/* Hover tooltip — rendered last so it always stacks on top
                 of every bubble and cluster popover, regardless of where
                 the hovered bubble sits in the draw order. */}
              {(() => {
                if (!hoveredId) return null;
                const pos = itemPositions.find((p) => p.item.id === hoveredId);
                if (!pos) return null;
                const item = pos.item;
                const zone = getZone(pos.xNorm, pos.yNorm, splitX);
                const bubbleColor = ZONE_META[zone].color;

                const ttW = 300;
                const ttH = 208;
                let ttX = pos.plotX + pos.radius + 12;
                if (ttX + ttW > padding + plotWidth - 4) {
                  ttX = pos.plotX - pos.radius - ttW - 12;
                }
                ttX = Math.max(
                  padding + 4,
                  Math.min(padding + plotWidth - ttW - 4, ttX),
                );
                let ttY = pos.plotY - ttH - 12;
                if (ttY < padding + 4) {
                  ttY = pos.plotY + pos.radius + 12;
                }
                ttY = Math.max(
                  padding + 4,
                  Math.min(padding + plotHeight - ttH - 4, ttY),
                );

                return (
                  <foreignObject
                    x={ttX}
                    y={ttY}
                    width={ttW}
                    height={ttH}
                    style={{ overflow: "visible", pointerEvents: "none" }}
                  >
                    <RiskTooltipCard
                      name={item.name}
                      appCode={item.appCode}
                      xLabel={xMetric.label}
                      xValue={xMetric.format(xMetric.getValue(item))}
                      yLabel={yMetric.label}
                      yValue={yMetric.format(yMetric.getValue(item))}
                      failedControls={item.riskCount}
                      sizeLabel={
                        orgScope ? "Applications (size)" : "Failed Controls (size)"
                      }
                      sizeValue={sizeOf(item)}
                      entityNoun={entityNoun}
                      coverageStatus={item.coverageStatus}
                      bubbleColor={bubbleColor}
                      zone={zone}
                      onMouseEnter={() => handleBubbleEnter(item.id)}
                      onMouseLeave={() => handleBubbleLeave(item.id)}
                      onViewApplication={() => {
                        setHoveredId(null);
                        onViewDetails(item.id, zone);
                      }}
                    />
                  </foreignObject>
                );
              })()}
            </svg>
          </div>
        </div>
        {/* Chart footer hint */}
        <div className="px-5 py-3 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.01] flex items-center justify-end gap-4 flex-shrink-0">
          <span className="text-[11px] text-[#94a3b8] dark:text-white/50">
            Click {orgScope ? "an organization" : "an application"} for
            control-level drill-down
          </span>
        </div>
      </div>

      {/* Side panels */}
      <aside className="lg:w-[360px] flex-shrink-0 flex flex-col gap-3">
        {/* Findings Distribution */}
        <FindingsDistributionPanel
          data={findingsDist}
          loading={findingsDistLoading}
        />

        {/* Top Risk Applications / Organizations.
            Both are the same list ranked by failed controls. The organization
            view drops the Trend column: drift is measured against a single
            baseline scan and only exists on application rows, so the column
            would be nothing but dashes. */}
        <div
          id={
            orgScope
              ? "top-risk-organizations-panel"
              : "top-risk-applications-panel"
          }
          data-name={`Top Risk ${entityNounPlural}`}
          className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.06)] dark:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] flex flex-col"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06]">
            <div className="inline-flex items-center gap-2">
              <Flame className="size-4 text-red-500" />
              <p className="text-sm font-semibold text-[#0f172a] dark:text-white">
                Top Risk {entityNounPlural}
              </p>
            </div>
            <div className="inline-flex items-center gap-2">
              {allItems.length > 0 && (
                <button
                  type="button"
                  onClick={() => setAllEntitiesOpen(true)}
                  className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  View More
                </button>
              )}
              <span className="inline-flex items-center justify-center min-w-6 h-5 px-1.5 rounded-md bg-red-50 dark:bg-red-500/[0.12] text-xs font-semibold tabular-nums text-red-700 dark:text-red-300">
                {topCritical.length}
              </span>
            </div>
          </div>
          {/* Column header */}
          <div
            className={`grid ${
              orgScope
                ? "grid-cols-[20px_10px_1fr_70px]"
                : "grid-cols-[20px_10px_1fr_70px_56px]"
            } gap-5 items-center px-4 py-1.5 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] text-[10px] font-semibold tracking-wider text-[#94a3b8] dark:text-white/45`}
          >
            <span>#</span>
            <span />
            <span>{entityNoun}</span>
            <span className="text-center">Failed Controls</span>
            {!orgScope && <span className="text-center">Trend</span>}
          </div>
          {topCritical.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <ShieldCheck className="size-5 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-[#0f172a] dark:text-white">
                All Within Tolerance
              </p>
              <p className="text-xs text-[#64748b] dark:text-white/45 mt-1">
                No critical {entityNounPlural.toLowerCase()} detected
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[#f1f5f9] dark:divide-white/[0.04] h-[156px] overflow-y-auto">
              {topCritical.map((item, idx) => {
                const ix = getXPosition(xMetric.getValue(item));
                const iy = getYPosition(yMetric.getValue(item));
                const itemZone = getZone(ix, iy, splitX);
                const zoneColor = ZONE_META[itemZone].color;
                const dotColor = zoneColor;
                const trend =
                  item.driftCount > 0
                    ? "worse"
                    : item.driftCount < 0
                      ? "better"
                      : "flat";
                const unmeasured = item.coverageStatus === "UNMEASURED";
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => onViewDetails(item.id, itemZone)}
                      className={`w-full text-left grid ${
                        orgScope
                          ? "grid-cols-[20px_10px_1fr_70px]"
                          : "grid-cols-[20px_10px_1fr_70px_56px]"
                      } gap-5 items-center px-4 py-1.5 hover:bg-[#f8fafc] dark:hover:bg-white/[0.03] transition-colors group`}
                    >
                      <span className="text-[11px] font-semibold tabular-nums text-[#94a3b8] dark:text-white/40">
                        {idx + 1}
                      </span>
                      <span
                        className="size-2.5 rounded-full"
                        style={{ background: dotColor }}
                      />
                      <span className="text-[12px] font-medium text-[#0f172a] dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors inline-flex items-center gap-1.5">
                        <TruncatedName name={item.name} className="truncate" />
                        {unmeasured && (
                          <AlertCircle
                            className="size-3 flex-shrink-0"
                            style={{ color: orgCoverageMeta("UNMEASURED").color }}
                            aria-label="Not assessed"
                          />
                        )}
                      </span>
                      <span
                        className="text-[13px] font-bold tabular-nums leading-none text-center"
                        style={{ color: zoneColor }}
                      >
                        {item.riskCount}
                      </span>
                      {!orgScope && (
                        <span className="inline-flex items-center justify-center">
                          {trend === "worse" && (
                            <ArrowUpRight className="size-3.5 text-red-500" />
                          )}
                          {trend === "better" && (
                            <TrendingDown className="size-3.5 text-emerald-500" />
                          )}
                          {trend === "flat" && (
                            <Minus className="size-3.5 text-[#cbd5e1] dark:text-white/30" />
                          )}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Applications without SSP. Absent in the organization view: an SSP
            binds to an application, so "organizations without SSP" has no
            like-for-like meaning. The coverage badge on each row carries that
            signal instead. */}
        {!orgScope && (
          <AppsWithoutSspPanel
            apps={appsWithoutSsp}
            loading={withoutSspLoading}
          />
        )}
      </aside>

      {/* Right-click context menu for precise threshold entry */}
      {menuPos && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setMenuPos(null)}
          onContextMenu={(e) => {
            e.preventDefault();
            setMenuPos(null);
          }}
        >
          <div
            className="absolute rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-[#131320] shadow-2xl p-3"
            style={{ left: menuPos.x, top: menuPos.y, minWidth: 220 }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[10px] font-semibold tracking-wider text-[#94a3b8] dark:text-white/45 mb-2">
              Set Threshold
            </p>
            <ThresholdControl value={threshold} onChange={onThresholdChange} />
            <button
              type="button"
              onClick={() => setMenuPos(null)}
              className="mt-3 w-full px-3 py-1.5 rounded-md text-xs font-semibold bg-[#f1f5f9] dark:bg-white/[0.06] text-[#475569] dark:text-white/70 hover:bg-[#e2e8f0] dark:hover:bg-white/[0.10] transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {allEntitiesOpen && (
        <AllEntitiesDrawer
          items={allItems}
          entityNoun={entityNoun}
          entityNounPlural={entityNounPlural}
          plottedCount={items.length}
          getZoneFor={(item) =>
            getZone(
              getXPosition(xMetric.getValue(item)),
              getYPosition(yMetric.getValue(item)),
              splitX,
            )
          }
          onSelect={(item, zone) => {
            setAllEntitiesOpen(false);
            onViewDetails(item.id, zone);
          }}
          onClose={() => setAllEntitiesOpen(false)}
        />
      )}
    </div>
  );
}

/* ── All entities drawer ───────────────────────────────────────────────────
   The plot draws only the worst QUADRANT_PLOT_TOP_N bubbles. This is where the
   rest stay reachable, so the cap narrows the chart without hiding anything.
   ──────────────────────────────────────────────────────────────────────── */

function AllEntitiesDrawer({
  items,
  entityNoun,
  entityNounPlural,
  plottedCount,
  getZoneFor,
  onSelect,
  onClose,
}: {
  items: RiskItem[];
  entityNoun: string;
  entityNounPlural: string;
  plottedCount: number;
  getZoneFor: (item: RiskItem) => ZoneKey;
  onSelect: (item: RiskItem, zone: ZoneKey) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = query.trim()
    ? items.filter((i) =>
        i.name.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : items;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex">
      <div
        className="absolute inset-0 bg-black/55 dark:bg-black/75 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative ml-auto w-full max-w-2xl bg-white dark:bg-gradient-to-br dark:from-[#131320] dark:to-[#0a0a14] shadow-2xl overflow-hidden flex flex-col">
        <div className="shrink-0 flex items-start justify-between gap-3 px-5 py-4 border-b border-[#e2e8f0] dark:border-white/[0.06]">
          <div className="min-w-0">
            <p className="text-lg font-bold text-[#0f172a] dark:text-white">
              All {entityNounPlural}
            </p>
            <p className="mt-0.5 text-xs text-[#64748b] dark:text-white/50">
              {items.length} in scope, ranked by failed controls. The quadrant
              plots the worst {plottedCount}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close all ${entityNounPlural.toLowerCase()}`}
            className="shrink-0 inline-flex items-center justify-center size-8 rounded-lg text-[#64748b] dark:text-white/55 hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="shrink-0 px-5 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06]">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${entityNounPlural.toLowerCase()}`}
            className="w-full rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] px-3 py-2 text-sm text-[#0f172a] dark:text-white placeholder:text-[#94a3b8] dark:placeholder:text-white/35 focus:outline-none focus:border-indigo-400"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="grid grid-cols-[28px_10px_1fr_90px] gap-4 items-center px-5 py-2 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] text-[10px] font-semibold tracking-wider text-[#94a3b8] dark:text-white/45">
            <span>#</span>
            <span />
            <span>{entityNoun}</span>
            <span className="text-center">Failed Controls</span>
          </div>
          {filtered.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-[#64748b] dark:text-white/50">
              No {entityNounPlural.toLowerCase()} match &ldquo;{query}&rdquo;.
            </p>
          ) : (
            <ul className="divide-y divide-[#f1f5f9] dark:divide-white/[0.04]">
              {filtered.map((item) => {
                const zone = getZoneFor(item);
                /* Rank is the position in the FULL ranking, not in the filtered
                   view, so searching never renumbers the list. */
                const rank = items.indexOf(item) + 1;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(item, zone)}
                      className="w-full text-left grid grid-cols-[28px_10px_1fr_90px] gap-4 items-center px-5 py-2 hover:bg-[#f8fafc] dark:hover:bg-white/[0.03] transition-colors"
                    >
                      <span className="text-[11px] font-semibold tabular-nums text-[#94a3b8] dark:text-white/40">
                        {rank}
                      </span>
                      <span
                        className="size-2.5 rounded-full"
                        style={{ background: ZONE_META[zone].color }}
                      />
                      <span className="min-w-0 truncate text-[13px] font-medium text-[#0f172a] dark:text-white">
                        {item.name}
                      </span>
                      <span className="text-center text-[13px] font-bold tabular-nums text-[#0f172a] dark:text-white">
                        {item.riskCount}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── Entity Details Drawer ─────────────────────────────────────────────── */

function EntityDetailsDrawer({
  entity,
  zone,
  open,
  onClose,
  loading,
  error = null,
  zIndexClass = "z-50",
}: {
  entity: EntityDetailsResponse | null;
  zone: ZoneKey | null;
  open: boolean;
  onClose: () => void;
  loading: boolean;
  /** Set when the drill-down could not be loaded. Previously this drawer had no
   *  failure path: it fell back to a zeroed record, which rendered as a titled
   *  but otherwise empty panel that read as real data. */
  error?: string | null;
  zIndexClass?: string;
}) {
  const [famExpanded, setFamExpanded] = useState(false);
  const [ctrlExpanded, setCtrlExpanded] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  useEffect(() => {
    setCompareOpen(false);
  }, [open, entity?.applicationId]);

  if (!open) return null;

  const famHasMore =
    (entity?.topFailingFamilies.length ?? 0) > DRILL_LIST_LIMIT;
  const ctrlHasMore =
    (entity?.topFailingControls.length ?? 0) > DRILL_LIST_LIMIT;

  const expandButton = (
    expanded: boolean,
    onToggle: () => void,
    remaining: number,
  ) => (
    <button
      type="button"
      onClick={onToggle}
      className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
    >
      {expanded ? "Show less" : `View ${remaining} more`}
    </button>
  );

  return createPortal(
    <div className={`fixed inset-0 ${zIndexClass} flex`}>
      <div
        className="absolute inset-0 bg-black/55 dark:bg-black/75 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <div className="relative ml-auto w-full max-w-2xl bg-white dark:bg-gradient-to-br dark:from-[#131320] dark:to-[#0a0a14] shadow-2xl overflow-hidden flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="size-5 animate-spin text-indigo-500 mr-2" />
            <span className="text-sm text-[#64748b] dark:text-white/55">
              Loading application details
            </span>
          </div>
        ) : !entity ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-8 text-center">
            <AlertCircle className="size-6 text-amber-500" />
            <p className="text-sm font-semibold text-[#0f172a] dark:text-white">
              Nothing to show
            </p>
            <p className="max-w-sm text-xs text-[#64748b] dark:text-white/50">
              {error ?? "No details are available for this application."}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-1 rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] px-3.5 py-2 text-xs font-semibold text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <DrillHeader
              entity={entity}
              onClose={onClose}
              comparing={compareOpen}
              onCompare={() => setCompareOpen((v) => !v)}
            />
            <div className="flex-1 overflow-y-auto">
              {compareOpen && entity.sspId ? (
              <CatalogCompareView
                sspId={entity.sspId}
                sspName={entity.sspName || entity.applicationName}
                sourceLabel={
                  entity.controlImplementationSummary?.frameworkCatalogTitle?.trim() ||
                  entity.controlImplementationSummary?.frameworkProfileTitle?.trim() ||
                  "Framework"
                }
                onBack={() => setCompareOpen(false)}
              />
              ) : (
              <>
              {/* Hero: Compliance Readiness | Risk Profile */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-5 pt-4">
                <ComplianceHeroBanner
                  summary={entity.controlImplementationSummary}
                />
                <RiskProfileCard entity={entity} zone={zone} />
              </div>
              {/* Metric tiles: Total Controls | Control Failures | Findings | Asset Coverage */}
              <div className="px-5 pt-4">
                <MetricTilesRow entity={entity} />
              </div>
              {/* Charts row: Implementation Status | Finding Distribution */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-5 py-4">
                <ImplementationStatusCard
                  summary={entity.controlImplementationSummary}
                />
                <FindingDistributionCard summary={entity.findingsSummary} />
              </div>
              {/* Bottom row: Top Failing Families | Top Failing Controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 border-t border-[#e2e8f0] dark:border-white/[0.06] md:divide-x md:divide-[#e2e8f0] md:dark:divide-white/[0.06]">
                <DrillSection
                  title="Top Failing Control Families"
                  action={
                    famHasMore
                      ? expandButton(
                          famExpanded,
                          () => setFamExpanded((v) => !v),
                          entity.topFailingFamilies.length - DRILL_LIST_LIMIT,
                        )
                      : undefined
                  }
                >
                  <ControlFamiliesPanel
                    families={entity.topFailingFamilies}
                    expanded={famExpanded}
                  />
                </DrillSection>
                <DrillSection
                  title="Top Failing Controls"
                  action={
                    ctrlHasMore
                      ? expandButton(
                          ctrlExpanded,
                          () => setCtrlExpanded((v) => !v),
                          entity.topFailingControls.length - DRILL_LIST_LIMIT,
                        )
                      : undefined
                  }
                >
                  <FailingControlsList
                    controls={entity.topFailingControls}
                    expanded={ctrlExpanded}
                  />
                </DrillSection>
              </div>
              {entity.worstAssets.length > 0 && (
                <div className="border-t border-[#e2e8f0] dark:border-white/[0.06]">
                  <DrillSection title="Most Impacted Assets" last>
                    <WorstAssetsList assets={entity.worstAssets} />
                  </DrillSection>
                </div>
              )}
              </>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

/* ── Drill-down sub-components ─────────────────────────────────────────── */

function critColor(level?: string | null): string {
  const v = (level ?? "").toUpperCase();
  if (v === "HIGH") return SEV_CRIT;
  if (v === "MEDIUM" || v === "MODERATE") return SEV_MED;
  if (v === "LOW") return SEV_LOW;
  return "#94a3b8";
}


function formatLevel(level?: string): string {
  const v = (level ?? "").trim();
  if (!v) return "—";
  return v
    .toLowerCase()
    .replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

function DrillHeader({
  entity,
  onClose,
  comparing,
  onCompare,
}: {
  entity: EntityDetailsResponse;
  onClose: () => void;
  comparing: boolean;
  onCompare: () => void;
}) {
  const navigate = useNavigate();
  const critC = critColor(entity.applicationCriticality);
  const sensC = critColor(entity.systemSensitivityLevel);
  const handleViewSsp = () => {
    if (!entity.sspId) return;
    navigate(`/continuous-compliance/system/system-security-plan/${entity.sspId}`);
    onClose();
  };
  const handleCompareSsp = () => {
    if (!entity.sspId) return;
    onCompare();
  };
  return (
    <div className="relative px-6 pt-5 pb-4 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-gradient-to-br from-white via-[#f8fafc] to-white dark:from-[#171728] dark:via-[#171728] dark:to-[#0e0e1a]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-500/40 to-transparent" />
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 space-y-2.5">
          <div className="flex items-baseline gap-2.5 flex-wrap min-w-0">
            <h2 className="text-xl font-bold tracking-tight text-[#0f172a] dark:text-white truncate">
              {entity.applicationName}
            </h2>
            {/* Names the kind of entity the drawer is showing. */}
            <span className="flex-shrink-0 rounded-md border border-[#e2e8f0] dark:border-white/[0.12] bg-[#f1f5f9] dark:bg-white/[0.06] px-1.5 py-0.5 text-[10px] font-bold tracking-[0.12em] text-[#64748b] dark:text-white/60">
              Application
            </span>
          </div>
          <div className="flex items-center gap-x-6 gap-y-1.5 flex-wrap text-[11px]">
            <div className="inline-flex items-center gap-1.5">
              <span className="text-[10px] font-bold tracking-[0.16em] text-[#64748b] dark:text-white/55">
                Sensitivity Level:
              </span>
              <span
                className="font-bold tracking-[0.08em]"
                style={{
                  color: entity.systemSensitivityLevel ? sensC : "#94a3b8",
                }}
              >
                {formatLevel(entity.systemSensitivityLevel)}
              </span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <span className="text-[10px] font-bold tracking-[0.16em] text-[#64748b] dark:text-white/55">
                Criticality:
              </span>
              <span
                className="font-bold tracking-[0.08em]"
                style={{
                  color: entity.applicationCriticality ? critC : "#94a3b8",
                }}
              >
                {formatLevel(entity.applicationCriticality)}
              </span>
            </div>
            {entity.sspId && (
              <button
                type="button"
                onClick={handleViewSsp}
                className="inline-flex items-center gap-1 font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
              >
                View SSP
                <ExternalLink className="size-3" />
              </button>
            )}
            {entity.sspId && (
              <button
                type="button"
                onClick={handleCompareSsp}
                aria-pressed={comparing}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                  comparing
                    ? "border border-indigo-300 dark:border-indigo-400/40 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/15"
                    : "bg-indigo-600 text-white shadow-sm hover:bg-indigo-700"
                }`}
              >
                <GitCompare className="size-3.5" />
                {comparing ? "Hide Comparison" : "Compare Catalogs"}
              </button>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 p-1.5 rounded-md text-[#64748b] dark:text-white/40 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] hover:text-[#0f172a] dark:hover:text-white transition-colors"
          aria-label="Close drawer"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}

function formatLongDate(d?: string): string {
  if (!d) return "baseline";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

const RISK_BAR_SEGMENTS: { key: string; label: string; color: string }[] = [
  { key: "s1", label: "Healthy", color: "#10b981" },
  { key: "s2", label: "Healthy", color: "#22c55e" },
  { key: "s3", label: "Improving", color: "#84cc16" },
  { key: "s4", label: "Moderate", color: "#eab308" },
  { key: "s5", label: "Emerging Risk", color: "#f59e0b" },
  { key: "s6", label: "High", color: "#f97316" },
  { key: "s7", label: "Critical", color: "#ef4444" },
];

function zoneToMarkerPct(zone: ZoneKey | null): number {
  if (!zone) return 50;
  if (zone === "HEALTHY") return 7;
  if (zone === "IMPROVING") return 35;
  if (zone === "EMERGING RISK") return 65;
  return 92;
}

function RiskProfileCard({
  entity,
  zone,
}: {
  entity: EntityDetailsResponse;
  zone: ZoneKey | null;
}) {
  const sens = entity.systemSensitivityLevel || "";
  const crit = entity.applicationCriticality || "";
  const sensC = sens ? critColor(sens) : "#94a3b8";
  const critC = crit ? critColor(crit) : "#94a3b8";
  const zoneMeta = zone ? ZONE_META[zone] : null;
  const overallLabel = zoneMeta?.label ?? "—";
  const overallC = zoneMeta?.color ?? "#94a3b8";
  const markerPct = zoneToMarkerPct(zone);

  return (
    <div
      id="risk-profile-card"
      data-name="Risk Profile"
      className="relative rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] px-4 py-3 overflow-hidden flex flex-col shadow-sm dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]"
    >
      <p className="text-sm font-semibold text-[#0f172a] dark:text-white mb-3">
        Risk Profile
      </p>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <p className="text-[10px] font-bold tracking-[0.12em] text-[#64748b] dark:text-white/55">
            Sensitivity
          </p>
          <p
            className="text-[12px] font-bold tracking-[0.06em] mt-1"
            style={{ color: sensC }}
          >
            {formatLevel(sens)}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold tracking-[0.12em] text-[#64748b] dark:text-white/55">
            Criticality
          </p>
          <p
            className="text-[12px] font-bold tracking-[0.06em] mt-1"
            style={{ color: critC }}
          >
            {formatLevel(crit)}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold tracking-[0.12em] text-[#64748b] dark:text-white/55">
            Overall Risk
          </p>
          <span
            className="inline-flex items-center justify-center px-1.5 py-0.5 mt-1 rounded text-[10px] font-bold tracking-[0.04em] border whitespace-nowrap flex-shrink-0"
            style={{
              color: overallC,
              borderColor: `${overallC}55`,
              background: `${overallC}14`,
            }}
            title={overallLabel}
          >
            {overallLabel}
          </span>
        </div>
      </div>
      <div className="mt-auto">
        <div className="relative">
          <div className="flex items-center overflow-hidden rounded-sm">
            {RISK_BAR_SEGMENTS.map((seg) => (
              <div
                key={seg.key}
                className="h-2.5 flex-1"
                style={{ background: seg.color }}
                title={seg.label}
              />
            ))}
          </div>
          {zone && (
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 size-3 rounded-full bg-white dark:bg-[#0f172a] border-2 shadow-sm pointer-events-none"
              style={{ left: `${markerPct}%`, borderColor: overallC }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

type IconType = typeof ShieldCheck;

function MetricTile({
  icon: Icon,
  iconColor,
  label,
  value,
  subtitleNode,
}: {
  icon: IconType;
  iconColor: string;
  label: string;
  value: string;
  subtitleNode?: ReactNode;
}) {
  return (
    <div className="relative rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] p-3 overflow-hidden shadow-sm dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className="inline-flex items-center justify-center size-7 rounded-md"
          style={{ background: `${iconColor}1F`, color: iconColor }}
        >
          <Icon className="size-4" />
        </span>
        <span className="text-[11px] font-semibold text-[#64748b] dark:text-white/55">
          {label}
        </span>
      </div>
      <p className="text-2xl font-bold tabular-nums leading-none text-[#0f172a] dark:text-white">
        {value}
      </p>
      {subtitleNode && (
        <div className="mt-1.5 text-[10px] leading-snug">{subtitleNode}</div>
      )}
    </div>
  );
}

function MetricTilesRow({ entity }: { entity: EntityDetailsResponse }) {
  const summary = entity.controlImplementationSummary;
  const total = summary?.totalProfileControls ?? 0;
  const implemented = summary?.claimedControlCount ?? 0;
  const fail = entity.currentFailedControlCount;
  const drift = entity.driftDelta;
  const findings = entity.findingsSummary;
  const totalAssets = entity.totalAssets;
  const assessed = entity.assetsWithCurrentScan;
  const coveragePct =
    totalAssets > 0 ? Math.round((assessed / totalAssets) * 100) : 0;
  const coverageColor =
    coveragePct >= 90 ? SEV_LOW : coveragePct >= 50 ? SEV_MED : SEV_CRIT;

  const driftLabel =
    drift > 0 ? "worsening" : drift < 0 ? "improving" : "no change";
  const driftColor = drift > 0 ? SEV_CRIT : drift < 0 ? SEV_LOW : "#94a3b8";

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <MetricTile
        icon={ShieldCheck}
        iconColor={SEV_LOW}
        label="Total Controls"
        value={total.toLocaleString()}
        subtitleNode={
          <span className="text-[#64748b] dark:text-white/55">
            {implemented.toLocaleString()} Implemented
          </span>
        }
      />
      <MetricTile
        icon={ShieldAlert}
        iconColor={SEV_CRIT}
        label="Control Failures"
        value={fail.toLocaleString()}
        subtitleNode={
          <span className="inline-flex items-center gap-0.5 flex-wrap text-[#64748b] dark:text-white/55">
            {drift > 0 && (
              <ArrowUpRight className="size-3" style={{ color: driftColor }} />
            )}
            {drift < 0 && (
              <TrendingDown className="size-3" style={{ color: driftColor }} />
            )}
            {drift === 0 && <Minus className="size-3" />}
            <span
              className="font-semibold tabular-nums"
              style={{ color: driftColor }}
            >
              {drift > 0 ? `+${drift}` : drift}
            </span>
            <span>{driftLabel}</span>
            <span className="opacity-70">
              since {formatLongDate(entity.baselineDay)}
            </span>
          </span>
        }
      />
      <MetricTile
        icon={FileWarning}
        iconColor={SEV_MED}
        label="Findings"
        value={findings.totalFindings.toLocaleString()}
        subtitleNode={
          <span className="text-[#64748b] dark:text-white/55">
            <span className="font-semibold text-red-600 dark:text-red-400 tabular-nums">
              {findings.openFindings}
            </span>{" "}
            open <span className="opacity-50">·</span>{" "}
            <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
              {findings.closedFindings}
            </span>{" "}
            closed
          </span>
        }
      />
      <MetricTile
        icon={Server}
        iconColor="#6366f1"
        label="Asset Coverage"
        value={`${assessed} / ${totalAssets}`}
        subtitleNode={
          <span className="font-semibold" style={{ color: coverageColor }}>
            {coveragePct}% assessed
          </span>
        }
      />
    </div>
  );
}

function FindingDistributionCard({ summary }: { summary: FindingsSummary }) {
  const total = summary.totalFindings;

  return (
    <div
      id="finding-distribution-chart"
      data-name="Finding Distribution"
      className="flex flex-col min-w-0"
    >
      <p className="text-sm font-semibold text-[#0f172a] dark:text-white mb-2.5">
        Finding Distribution
      </p>
      <div className="relative rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] p-4 flex-1 shadow-sm dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
        {total === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-4">
            <span
              className="inline-flex items-center justify-center size-10 rounded-full mb-2"
              style={{ background: `${SEV_LOW}1F`, color: SEV_LOW }}
            >
              <ShieldCheck className="size-5" />
            </span>
            <p className="text-[12px] font-semibold text-[#0f172a] dark:text-white">
              No findings recorded
            </p>
            <p className="text-[10px] text-[#64748b] dark:text-white/55 mt-0.5">
              0 open · 0 closed
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-3 h-full">
            <DonutBySeverity summary={summary} />
            <div className="flex-1 min-w-0 space-y-1">
              {SEVERITY_ORDER.map((s) => {
                const count = summary.bySeverity[s.key] ?? 0;
                if (count === 0) return null;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div
                    key={s.key}
                    className="flex items-center gap-1.5 text-[11px]"
                  >
                    <span
                      className="size-2 rounded-full flex-shrink-0"
                      style={{ background: s.color }}
                    />
                    <span className="text-[#475569] dark:text-white/65 flex-1 truncate">
                      {s.label}
                    </span>
                    <span
                      className="font-bold tabular-nums"
                      style={{ color: s.color }}
                    >
                      {count}
                    </span>
                    <span className="text-[10px] text-[#94a3b8] dark:text-white/40 tabular-nums w-9 text-right">
                      ({pct}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DonutBySeverity({ summary }: { summary: FindingsSummary }) {
  const total = summary.totalFindings;
  const size = 96;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  const segments = SEVERITY_ORDER.map((s) => {
    const count = summary.bySeverity[s.key] ?? 0;
    if (count === 0 || total === 0) return null;
    const len = (count / total) * circumference;
    const seg = {
      key: s.key,
      color: s.color,
      dash: len,
      gap: circumference - len,
      rotate: (offset / circumference) * 360,
    };
    offset += len;
    return seg;
  });

  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          className="stroke-[#f1f5f9] dark:stroke-white/[0.07]"
          strokeWidth={stroke}
        />
        {segments.map(
          (seg) =>
            seg && (
              <circle
                key={seg.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={seg.color}
                strokeWidth={stroke}
                strokeLinecap="butt"
                strokeDasharray={`${seg.dash} ${seg.gap}`}
                transform={`rotate(${-90 + seg.rotate} ${size / 2} ${size / 2})`}
              />
            ),
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <p className="text-xl font-bold tabular-nums leading-none text-[#0f172a] dark:text-white">
          {total}
        </p>
        <p className="text-[9px] font-semibold tracking-wider text-[#64748b] dark:text-white/55 mt-0.5">
          Total
        </p>
      </div>
    </div>
  );
}

const IMPL_STATUS_META: Record<string, { label: string; color: string }> = {
  implemented: { label: "Implemented", color: "#10b981" },
  alternative: { label: "Alternative", color: "#6366f1" },
  partial: { label: "Partial", color: "#f59e0b" },
  planned: { label: "Planned", color: "#3b82f6" },
  "not-implemented": { label: "Not Implemented", color: "#ef4444" },
  unspecified: { label: "Unspecified", color: "#94a3b8" },
};

const IMPL_STATUS_ORDER = [
  "implemented",
  "alternative",
  "partial",
  "planned",
  "not-implemented",
  "unspecified",
];

function statusMeta(key?: string | null): { label: string; color: string } {
  const k = String(key ?? "");
  return (
    IMPL_STATUS_META[k] ?? {
      label: k ? k.charAt(0).toUpperCase() + k.slice(1).replace(/-/g, " ") : "Unspecified",
      color: "#94a3b8",
    }
  );
}

function ComplianceHeroBanner({
  summary,
}: {
  summary: ControlImplementationSummary;
}) {
  const total = summary?.totalProfileControls ?? 0;
  const implemented = summary?.claimedControlCount ?? 0;
  const fallbackPct = total > 0 ? Math.round((implemented / total) * 100) : 0;
  const pct =
    typeof summary?.complianceScore === "number"
      ? Math.round(summary.complianceScore)
      : fallbackPct;
  const color = complianceScoreColor(pct);

  const catalogName =
    summary?.frameworkCatalogTitle?.trim() ||
    summary?.frameworkProfileTitle?.trim() ||
    "Framework";
  const profileSubtitle =
    summary?.frameworkProfileTitle &&
    summary?.frameworkCatalogTitle &&
    summary.frameworkProfileTitle.trim() !==
      summary.frameworkCatalogTitle.trim()
      ? summary.frameworkProfileTitle
      : null;

  const ratingLabel =
    pct >= 90
      ? "Excellent"
      : pct >= 70
        ? "On Track"
        : pct >= 50
          ? "Needs Attention"
          : "Critical";

  return (
    <div
      id="compliance-score-card"
      data-name="Compliance Score"
      className="relative rounded-lg border px-4 py-3 overflow-hidden flex flex-col shadow-sm dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]"
      style={{
        background: `linear-gradient(135deg, ${color}0F 0%, transparent 100%)`,
        borderColor: `${color}33`,
      }}
    >
      <div
        className="absolute top-0 right-0 size-20 rounded-full opacity-20 blur-xl pointer-events-none"
        style={{ background: color }}
      />

      <div className="relative flex items-center gap-3 flex-wrap sm:flex-nowrap flex-1">
        {/* Left — percentage centered with score label below */}
        <div className="flex-shrink-0 flex flex-col items-center text-center">
          <p
            className="font-bold tabular-nums leading-none tracking-tight"
            style={{ color, fontSize: 44 }}
          >
            {pct}
            <span className="text-[20px] font-bold ml-0.5">%</span>
          </p>
          <p className="mt-1.5 text-[9px] font-semibold tracking-wider text-[#94a3b8] dark:text-white/45 whitespace-nowrap">
            Compliance Score
          </p>
        </div>

        {/* Right — catalog title (with rating pill) + profile name below */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p
              className="text-[15px] font-bold tracking-tight text-[#0f172a] dark:text-white truncate min-w-0"
              title={catalogName}
            >
              {catalogName}
            </p>
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider flex-shrink-0"
              style={{
                background: `${color}14`,
                color,
                border: `1px solid ${color}33`,
              }}
            >
              <span
                className="size-1 rounded-full"
                style={{
                  background: color,
                  boxShadow: `0 0 4px ${color}`,
                }}
              />
              {ratingLabel}
            </span>
          </div>
          {profileSubtitle && (
            <p
              className="text-[11px] text-[#64748b] dark:text-white/55 truncate mt-1"
              title={profileSubtitle}
            >
              {profileSubtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ImplementationStatusCard({
  summary,
}: {
  summary: ControlImplementationSummary;
}) {
  const total = summary?.totalProfileControls ?? 0;

  const statusEntries = useMemo(() => {
    const map = summary?.byImplementationStatus ?? {};
    const known = IMPL_STATUS_ORDER.filter((k) => (map[k] ?? 0) > 0).map(
      (k) => ({ key: k, count: map[k] }),
    );
    const extras = Object.keys(map)
      .filter((k) => !IMPL_STATUS_ORDER.includes(k) && (map[k] ?? 0) > 0)
      .map((k) => ({ key: k, count: map[k] }));
    return [...known, ...extras];
  }, [summary?.byImplementationStatus]);

  const statusTotal = statusEntries.reduce((s, e) => s + e.count, 0);

  return (
    <div
      id="control-implementation-status-chart"
      data-name="Control Implementation Status"
      className="flex flex-col min-w-0"
    >
      <p className="text-sm font-semibold text-[#0f172a] dark:text-white mb-2.5">
        Control Implementation Status
      </p>
      <div className="relative rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] p-4 flex-1 shadow-sm dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
        {total === 0 ? (
          <div className="flex items-center justify-center h-full text-[11px] text-[#94a3b8] dark:text-white/40 italic py-6">
            No control data
          </div>
        ) : (
          <div className="flex items-center justify-center gap-3 h-full">
            <ImplementationDonut
              total={total}
              entries={statusEntries}
              statusTotal={statusTotal}
            />
            <div className="flex-1 min-w-0 space-y-1">
              {statusEntries.map((e) => {
                const meta = statusMeta(e.key);
                const pct =
                  statusTotal > 0
                    ? Math.round((e.count / statusTotal) * 100)
                    : 0;
                return (
                  <div
                    key={e.key}
                    className="flex items-center gap-1.5 text-[11px]"
                  >
                    <span
                      className="size-2 rounded-full flex-shrink-0"
                      style={{ background: meta.color }}
                    />
                    <span className="text-[#475569] dark:text-white/65 flex-1 truncate">
                      {meta.label}
                    </span>
                    <span
                      className="font-bold tabular-nums"
                      style={{ color: meta.color }}
                    >
                      {e.count}
                    </span>
                    <span className="text-[10px] text-[#94a3b8] dark:text-white/40 tabular-nums w-9 text-right">
                      ({pct}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ImplementationDonut({
  total,
  entries,
  statusTotal,
}: {
  total: number;
  entries: { key: string; count: number }[];
  statusTotal: number;
}) {
  const size = 96;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  const segments = entries
    .map((e) => {
      if (e.count === 0 || statusTotal === 0) return null;
      const meta = statusMeta(e.key);
      const len = (e.count / statusTotal) * circumference;
      const seg = {
        key: e.key,
        color: meta.color,
        dash: len,
        gap: circumference - len,
        rotate: (offset / circumference) * 360,
      };
      offset += len;
      return seg;
    })
    .filter(Boolean);

  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          className="stroke-[#f1f5f9] dark:stroke-white/[0.07]"
          strokeWidth={stroke}
        />
        {segments.map(
          (seg) =>
            seg && (
              <circle
                key={seg.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={seg.color}
                strokeWidth={stroke}
                strokeLinecap="butt"
                strokeDasharray={`${seg.dash} ${seg.gap}`}
                transform={`rotate(${-90 + seg.rotate} ${size / 2} ${size / 2})`}
              />
            ),
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <p className="text-xl font-bold tabular-nums leading-none text-[#0f172a] dark:text-white">
          {total}
        </p>
        <p className="text-[9px] font-semibold tracking-wider text-[#64748b] dark:text-white/55 mt-0.5">
          Total
        </p>
      </div>
    </div>
  );
}

function DrillSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  tight?: boolean;
  last?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="px-5 py-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[14px] font-bold tracking-tight text-[#0f172a] dark:text-white">
          {title}
        </p>
        {action}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

/* ── Findings Summary ──────────────────────────────────────────────────── */

const SEVERITY_ORDER: { key: string; label: string; color: string }[] = [
  { key: "critical", label: "Critical", color: SEV_CRIT },
  { key: "high", label: "High", color: SEV_HIGH },
  { key: "medium", label: "Medium", color: SEV_MED },
  { key: "low", label: "Low", color: "#3b82f6" },
  { key: "unspecified", label: "Unspecified", color: "#94a3b8" },
];

const DRILL_LIST_LIMIT = 5;

function ControlFamiliesPanel({
  families,
  expanded,
}: {
  families: FailingFamily[];
  expanded: boolean;
}) {
  if (families.length === 0) {
    return (
      <p className="text-[11px] text-[#94a3b8] dark:text-white/40 italic">
        No data
      </p>
    );
  }
  const max = Math.max(...families.map((f) => f.failedControlCount), 1);
  const visible = expanded ? families : families.slice(0, DRILL_LIST_LIMIT);
  return (
    <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] shadow-sm dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
      <div className="grid grid-cols-[68px_1fr_56px] gap-3 items-center px-3 h-8 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] text-[10px] font-semibold tracking-wider text-[#94a3b8] dark:text-white/45">
        <span>Family</span>
        <span>Group</span>
        <span className="text-right">Controls</span>
      </div>
      <ul
        className={`divide-y divide-[#f1f5f9] dark:divide-white/[0.04] h-[270px] ${expanded ? "overflow-y-auto" : "overflow-hidden"}`}
      >
        {visible.map((f) => {
          const pct = (f.failedControlCount / max) * 100;
          const code = (f.family ?? "").toUpperCase();
          const groupName = f.groupName?.trim() || code;
          return (
            <li
              key={f.family}
              className="h-[54px] px-3 flex flex-col justify-center gap-2 transition-colors hover:bg-[#f8fafc] dark:hover:bg-white/[0.02]"
            >
              <div className="grid grid-cols-[68px_1fr_56px] gap-3 items-center">
                <span
                  className="inline-flex items-center justify-center h-5 w-full px-1.5 rounded-md text-[10px] font-bold tracking-[0.04em] tabular-nums whitespace-nowrap overflow-hidden bg-[#0f172a]/[0.04] dark:bg-white/[0.06] text-[#334155] dark:text-white/75 border border-[#0f172a]/[0.06] dark:border-white/[0.06]"
                  title={`Family ${code}`}
                >
                  {code}
                </span>
                <p
                  className="text-[12px] font-medium text-[#0f172a] dark:text-white truncate"
                  title={groupName}
                >
                  {groupName}
                </p>
                <span className="text-[13px] font-bold tabular-nums leading-none text-[#0f172a] dark:text-white text-right">
                  {f.failedControlCount}
                </span>
              </div>
              <div className="grid grid-cols-[68px_1fr_56px] gap-3 items-center">
                <span />
                <div className="h-1.5 rounded-full bg-[#f1f5f9] dark:bg-white/[0.06] overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pct}%`,
                      background: `linear-gradient(90deg, ${SEV_CRIT}, ${SEV_HIGH})`,
                    }}
                  />
                </div>
                <span />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function controlStateStyle(state: ControlState): {
  bg: string;
  color: string;
  border: string;
} {
  if (state === "NEW")
    return {
      bg: `${SEV_CRIT}1A`,
      color: SEV_CRIT,
      border: `${SEV_CRIT}33`,
    };
  if (state === "PERSISTENT")
    return {
      bg: `${SEV_HIGH}1A`,
      color: SEV_HIGH,
      border: `${SEV_HIGH}33`,
    };
  return { bg: `${SEV_LOW}1A`, color: SEV_LOW, border: `${SEV_LOW}33` };
}

function FailingControlsList({
  controls,
  expanded,
}: {
  controls: FailingControl[];
  expanded: boolean;
}) {
  if (controls.length === 0) {
    return (
      <p className="text-[11px] text-[#94a3b8] dark:text-white/40 italic">
        No data
      </p>
    );
  }
  const visible = expanded ? controls : controls.slice(0, DRILL_LIST_LIMIT);
  return (
    <div>
      <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] shadow-sm dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
        {/* Column header — same pattern as AppsWithoutSspPanel on the main page */}
        <div className="grid grid-cols-[104px_1fr_56px] gap-3 items-center px-3 h-8 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] text-[10px] font-semibold tracking-wider text-[#94a3b8] dark:text-white/45">
          <span>Control</span>
          <span>Title</span>
          <span className="text-right">Assets</span>
        </div>
        <ul
          className={`divide-y divide-[#f1f5f9] dark:divide-white/[0.04] h-[270px] ${expanded ? "overflow-y-auto" : "overflow-hidden"}`}
        >
          {visible.map((c) => {
            const st = controlStateStyle(c.state);
            const code = (c.controlId ?? "").toUpperCase();
            const title = (c.controlTitle || c.controlName || "").trim();
            return (
              <li
                key={c.controlId}
                className="grid grid-cols-[104px_1fr_56px] gap-3 items-center px-3 h-[54px] transition-colors hover:bg-[#f8fafc] dark:hover:bg-white/[0.02]"
              >
                {/* Control codes run as long as "TRMG-11.1.6", so the badge
                    fills the column and keeps the code on one line instead of
                    wrapping out of the pill. */}
                <span
                  className="inline-flex items-center justify-center h-5 w-full px-1.5 rounded-md text-[10px] font-bold font-mono tracking-[0.02em] whitespace-nowrap overflow-hidden"
                  style={{
                    background: st.bg,
                    color: st.color,
                    border: `1px solid ${st.border}`,
                  }}
                  title={`${c.state} · ${code}`}
                >
                  {code}
                </span>
                <p
                  className="text-[12px] font-medium text-[#0f172a] dark:text-white truncate"
                  title={title || code}
                >
                  {title || (
                    <span className="text-[#94a3b8] dark:text-white/40 italic">
                      Untitled control
                    </span>
                  )}
                </p>
                <span className="text-[13px] font-bold tabular-nums leading-none text-[#0f172a] dark:text-white text-right">
                  {c.failedAssetCount}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function WorstAssetsList({ assets }: { assets: WorstAsset[] }) {
  if (assets.length === 0) {
    return <p className="text-xs text-[#94a3b8] dark:text-white/40">No data</p>;
  }
  const max = Math.max(...assets.map((a) => a.failedControlCount), 1);
  return (
    <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] shadow-sm dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
      <ul className="divide-y divide-[#f1f5f9] dark:divide-white/[0.04]">
        {assets.map((a) => {
          const pct = (a.failedControlCount / max) * 100;
          const color =
            a.failedControlCount >= 7
              ? SEV_CRIT
              : a.failedControlCount >= 3
                ? SEV_HIGH
                : SEV_MED;
          return (
            <li key={a.assetId} className="px-3 py-2">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[#0f172a] dark:text-white truncate">
                    {a.hostname}
                  </p>
                  <p className="text-[10px] font-mono text-[#64748b] dark:text-white/50">
                    {a.ipAddress}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span
                    className="text-sm font-bold tabular-nums"
                    style={{ color }}
                  >
                    {a.failedControlCount}
                  </span>
                  <span className="block text-[9px] text-[#94a3b8] dark:text-white/40 leading-none">
                    failed
                  </span>
                </div>
              </div>
              <div className="h-1 rounded-full bg-[#f1f5f9] dark:bg-white/[0.06] overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, background: color }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
